import type { StaffMessages } from './types'

export const en: StaffMessages = {
  meta: {
    language: 'English',
    htmlLang: 'en',
    title: 'Mazzella Rent · Staff',
  },

  app: {
    name: 'Mazzella Rent',
    product: 'Staff',
    skipToContent: 'Skip to content',
    loading: 'Loading…',
    retry: 'Try again',
    notFound: 'This page does not exist.',
    home: 'Back to Today',
    technicalDetails: 'Technical details',
    notActiveYet: 'Not active yet',
    stripeCode: 'Stripe code: {code}',
  },

  lists: {
    pages: 'Result pages',
    range: '{first}–{last}',
    change: 'Change the search, or clear it.',
    pastEnd: 'Nothing on this page.',
  },

  nav: {
    label: 'Main menu',
    today: 'Today',
    bookings: 'Bookings',
    devices: 'Devices',
    people: 'People',
    lock: 'Lock',
    changePin: 'Change PIN',
    changePassword: 'Change password',
    signedInAs: '{name}, {role}',
    otherLanguage: 'Italiano',
    otherLanguageLabel: 'Usa l’app in italiano',
    newBooking: 'New booking',
    timeline: 'Timeline',
    prices: 'Prices',
    kit: 'Emergency kit',
    vehicles: 'Vehicles',
    customers: 'Customers',
    maintenance: 'Maintenance',
    models: 'Categories and models',
    cash: 'Cash',
    fiscal: 'Fiscal',
    fines: 'Fines',
    reports: 'Reports',
    reconcile: 'Reconciliation',
    system: 'System',
    terms: 'Contract rules',
    groupDesk: 'Counter',
    groupFleet: 'Fleet and customers',
    groupMoney: 'Money',
    groupAdmin: 'Admin',
    menu: 'Menu',
    closeMenu: 'Close the menu',
    search: 'Find a booking',
    searchSubmit: 'Find',
    device: 'Device: {name}',
    offline: 'No connection',
    /** UI-2g: said for a few seconds when the connection comes back. */
    backOnline: 'Connection back.',
  },

  roles: {
    owner: 'Owner',
    agent: 'Counter',
    runner: 'Runner',
  },

  enroll: {
    title: 'Enroll this device',
    intro:
      'Sign in once with e-mail and password: the device stays enrolled and from then on unlocks with a personal PIN.',
    revoked:
      'This device has been signed out. To use it again, enroll it with e-mail and password.',
    email: 'E-mail',
    password: 'Password',
    deviceName: 'Device name',
    deviceNameHint: 'As it will appear in the device list, for example “Hydrofoil counter iPad”.',
    totp: 'Authenticator app code',
    totpHint: 'The 6 digits the app shows right now.',
    submit: 'Enroll and sign in',
  },

  signIn: {
    title: 'Sign in with your password',
    intro: 'For anyone without a PIN yet, or locked out of it on this device.',
    submit: 'Sign in',
    backToLock: 'Back to the lock screen',
  },

  lock: {
    title: 'Screen locked',
    device: 'Device: {name}',
    pickUser: 'Choose your name, then enter your PIN.',
    noPin: 'No PIN',
    lockedHere: 'Locked here',
    pinFor: 'PIN for {name}',
    pinLabel: 'PIN, 6 digits',
    unlock: 'Unlock',
    needSix: 'Type the 6 digits of the PIN.',
    /** UI-2a: in place of a person's PIN, while nobody is chosen; the keypad's reason too. */
    chooseFirst: 'Choose your name',
    /** UI-2a: the three who last unlocked this iPad are on its lock screen; everyone, from here. */
    everyone: 'Other people',
    back: 'Back',
    usePassword: 'Sign in with your password',
    wrongPin: {
      one: 'Wrong PIN: {count} attempt left on this device.',
      other: 'Wrong PIN: {count} attempts left on this device.',
    },
    autoLocked: 'Locked after {minutes} minutes without activity.',
    handback: 'The device is back with you: unlock with your PIN to carry on.',
    expired: 'The session has ended: unlock with your PIN to carry on.',
    keypad: 'PIN keypad',
    deleteDigit: 'Delete the last digit',
    /** UI-1e: the dots are drawn, not read: how many digits are in, said as it changes. */
    digits: { one: '{count} digit of 6', other: '{count} digits of 6' },
  },

  /** UI-1d (C7): the customer's screen when the contract does not open; the staff's words. */
  signingScreen: {
    unavailable: 'The contract will not open',
    takeBack: 'Take the device back',
  },

  pin: {
    title: 'Choose your PIN',
    intro: 'Six digits, yours alone: they unlock every enrolled device. No sequences or repeats.',
    pin: 'New PIN',
    confirm: 'Repeat the PIN',
    mismatch: 'The two PINs do not match.',
    format: 'The PIN has 6 digits.',
    submit: 'Save the PIN',
    saved: 'PIN saved.',
    later: 'Later',
    needsPassword:
      'A PIN is chosen within 10 minutes of a password sign-in: lock the screen and sign in with your password.',
  },

  password: {
    title: 'Change password',
    intro:
      'The password enrols a device and signs you in without a PIN: 12 characters or more, and not your e-mail.',
    forcedTitle: 'Choose your password',
    forcedIntro:
      'The password you were given works for the first sign-in only: to carry on, choose one of your own, 12 characters or more and not your e-mail.',
    current: 'Current password',
    received: 'Password you were given',
    next: 'New password',
    nextHint: 'A phrase of several words is easy to remember and hard to guess.',
    confirm: 'Repeat the new password',
    missingCurrent: 'Type the current password.',
    wrongCurrent: 'That password is not the right one: type it again.',
    short: 'The new password has 12 characters or more.',
    mismatch: 'The two passwords do not match.',
    submit: 'Save the password',
    done: 'Password changed: from now on, sign in with the new one.',
  },

  update: {
    title: 'Update needed',
    app: 'This version of the app is no longer accepted. Update it from the App Store to carry on.',
    web: 'A new version is available. Reload the page to carry on.',
    reload: 'Reload the page',
  },

  today: {
    title: 'Today',
    day: 'Day',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    backToToday: 'Back to today',
    location: 'Location',
    allLocations: 'All locations',
    locationHint: 'Deliveries are always shown.',
    pickups: 'Pick-ups',
    returns: 'Returns',
    overdueReturns: 'Overdue returns',
    missedPickups: 'Missed pick-ups',
    overbooked: 'Confirmed with no car for them',
    expiringDocuments: 'Vehicle documents expiring',
    depositAlerts: 'Deposits lapsing',
    moneyToReturn: 'To give back',
    cargos: 'CaRGOS to sort out',
    fines: 'Fines due',
    cargosStatus: {
      refused: 'Report refused',
      unknown: 'Outcome unknown',
      queued: 'Report stuck',
    },
    moneyToReturnAmount: '{amount} to give back',
    refundFailed: 'The refund through Stripe failed',
    refundAction: 'Stripe needs an action on the refund',
    refundDisputed: 'Disputed: the customer’s bank decides',
    depositLapses: 'The hold lapses on {date}',
    depositLapsed: 'The hold has lapsed',
    nothing: 'Nothing scheduled.',
    noDocuments: 'No document expires in the next 30 days.',
    late: 'Late',
    unassigned: 'Vehicle not assigned',
    checkAtCounter: 'Check at the counter',
    ferry: 'Ferry {name}',
    ferryArrival: 'arrives {time}',
    expiresIn: {
      one: 'expires in {count} day',
      other: 'expires in {count} days',
    },
    expiresToday: 'expires today',
    expiredAgo: {
      one: 'expired {count} day ago',
      other: 'expired {count} days ago',
    },
    refresh: 'Refresh',
    updatedAt: 'Updated at {time}',
    attention: 'To check',
    attentionClear: 'Nothing to check.',
    dayLine: {
      title: 'The day',
      summary: 'From {from} to {to}: {items}.',
      nowAt: 'It is now {time}.',
      pickups: { one: '{count} pick-up', other: '{count} pick-ups' },
      returns: { one: '{count} return', other: '{count} returns' },
      ferries: {
        one: '{count} ferry arrival',
        other: '{count} ferry arrivals',
      },
      legendPickups: 'Pick-ups',
      legendReturns: 'Returns',
      legendFerries: 'Ferry arrivals',
      legendNow: 'Now',
    },
  },

  /**
   * S9 (LOC-5, J8): the trips to and from the delivery zones, on Today and on
   * a booking's page. Each trip is named as its price line names it.
   */
  trips: {
    title: 'Trips',
    toAssign: 'Trips to assign',
    none: 'No trips.',
    state: {
      to_assign: 'To assign',
      assigned: 'Given to {name}',
      done: 'Done',
    },
    mine: 'Yours',
    meetingPoint: 'Meeting point: {place}',
    note: 'Note: {note}',
    assign: 'Assign',
    edit: 'Edit',
    form: {
      about: 'Booking {code}, {customer}: {when}.',
      driver: 'Who drives it',
      nobody: 'Nobody',
      driverHint: 'Among the active people who hand vehicles over.',
      meetingPoint: 'Meeting point',
      meetingPointHint: 'Where the customer is met: a quay, a hotel’s door.',
      note: 'Note',
      ferryName: 'The customer’s ferry',
      ferryNameHint: 'Only if they arrive on a different ferry from the booking’s.',
      bookingFerry: 'On the booking: {ferry}.',
      ferryTime: 'Ferry arrival',
      ferryTimeHint: 'The time, on the day of the trip.',
      ferryTimeInvalid: 'Type a valid time, for example 09:30.',
      save: 'Save',
      cancel: 'Cancel',
      saved: 'Trip saved.',
    },
  },

  bookings: {
    title: 'Bookings',
    search: 'Search',
    searchHint: 'Code, name, e-mail, phone, plate or old-site reference.',
    status: 'Status',
    anyStatus: 'Any status',
    from: 'Pick-up from',
    to: 'Pick-up until',
    submit: 'Search',
    clear: 'Clear',
    upcoming: 'Pick-ups from today on, soonest first.',
    found: {
      one: '{count} booking',
      other: '{count} bookings',
    },
    range: '{first}–{last} of {total}',
    previous: 'Previous',
    next: 'Next',
    none: 'No booking found.',
    noneUpcoming: 'No pick-ups from today on.',
    fills: 'Bookings from the website and the counter show up here.',
  },

  booking: {
    title: 'Booking {code}',
    back: 'Bookings',
    notFound: 'No booking has this code.',
    summary: 'Summary',
    pickup: 'Pick-up',
    return: 'Return',
    category: 'Category',
    vehicle: 'Vehicle',
    source: 'Channel',
    // S9 (ADM-4): a booking carried over from the old site, by the reference its customer knows.
    oldSite: 'Old site {reference}',
    oldSiteNote:
      'A booking from the old site: no automatic e-mail goes to the customer, write to them yourself quoting reference {reference}. The old site’s terms apply: a cancellation fee is typed by hand.',
    rentalDays: {
      one: '{count} rental day',
      other: '{count} rental days',
    },
    total: 'Total',
    listPrice: 'List price',
    customer: 'Customer',
    name: 'Name',
    email: 'E-mail',
    phone: 'Phone',
    nationality: 'Nationality',
    birthDate: 'Date of birth',
    notGiven: 'Not given',
    driver: 'Driver',
    toCheck: 'To check at the counter:',
    documents: 'Documents',
    price: 'Price',
    quoteUnreadable: 'The price breakdown cannot be read: the total stands.',
    terms: 'Terms accepted: version {version}.',
    noTerms: 'Terms not accepted yet.',
    oldSiteTerms: 'The old site’s terms apply.',
    termsSignedAtCheckout: 'Contract signed at hand-over (terms version {version}).',
    contract: {
      title: 'Contract',
      badge: 'Signed on the device',
      none: 'Not signed yet: the customer signs on the device at hand-over.',
      prepared: 'Waiting for the customer to sign on the device.',
      signed: 'Signed by the customer on the device on {date}.',
      stale: 'The booking changed after signing: the contract must be signed again.',
      pdf: 'Open the signed contract (PDF)',
      pdfPending: 'The contract PDF is being prepared.',
      pdfNotInApp:
        'The contract PDF cannot open in this app right now: close it and open it again, or open the PDF from a computer.',
      // S8: a booking naming someone anonymised.
      ownerOnly: 'This booking names an anonymised person: only the Owner opens its contract.',
      viewLogged: 'This booking names an anonymised person: every view of its contract is logged.',
    },
    notes: 'Notes',
    confirmedAt: 'Confirmed: {date}',
    payment: 'Payment',
    payAtCounter: 'The customer pays at the counter, at pick-up.',
    prepayOnline:
      'It is paid online on the website: nothing is taken at the counter until the online payment confirms it.',
    paidInFull: 'Already paid in full: there is nothing to collect.',
    feeAtCounter: 'The cancellation fee is still owed: take it at the counter.',
    nothingDue: 'There is nothing to collect.',
    owedAgain:
      'The customer’s bank gave a payment back: what is owed again is taken at the counter only.',
    fineFeeLink:
      'The fines’ admin fees are still owed: take them at the counter or by payment link.',
    fineFeeLinkPending:
      'Payment link: the fines’ admin fees are still owed; take them at the counter.',
    fineFeeAtCounter:
      'The fines’ admin fees are still owed: for this booking they are taken at the counter only.',
    paymentLinkPending: 'Payment link: the customer pays at the counter, at pick-up.',
    paymentLink: 'Payment link',
    paymentLinkIntro:
      'The customer pays the balance, {amount}, online from a link valid for 7 days. It shows the amount only, not the booking’s details.',
    paymentLinkEmail: 'Send by e-mail',
    paymentLinkMake: 'Make a link to copy',
    paymentLinkNoEmail:
      'The booking has no e-mail address: make the link and copy it into the message.',
    paymentLinkEmailed: 'Link sent by e-mail. Valid until {date}.',
    paymentLinkMade: 'Link ready: copy it into the WhatsApp message. Valid until {date}.',
    paymentLinkField: 'Payment link to copy',
    paymentLinkCopy: 'Copy',
    paymentLinkCopied: 'Copied.',
    paymentLinkLastEmailed: 'Last link sent by e-mail: {date}.',
    paymentLinkLastMade: 'Last link made: {date}.',
    priceOverridden: 'Manual price: the list price was {quoted}. Reason: {reason}',
    created: 'Booking {code} confirmed.',
    forCustomer: 'New booking for this customer',
    /** OPS-1: the move that is not a drag: the vehicle and the dates, from the booking page. */
    move: {
      action: 'Move…',
      title: 'Move booking {code}',
      hint: 'A vehicle of the same category and the pick-up and return dates, together or alone. Availability is checked before saving.',
      keep: 'As it is now: {plate}',
      keepNone: 'As it is now: no vehicle',
      unchanged: 'Nothing has changed.',
      invalidDates: 'The dates and times must be complete.',
      returnBeforePickup: 'The return must come after the pick-up.',
    },
  },

  devices: {
    title: 'Devices',
    intro: 'The enrolled devices, who is using them and when they were last active.',
    current: 'This device',
    enrolled: 'Enrolled by {name}, {date}',
    lastSeen: 'Last active: {date}',
    inUse: 'In use: {name}',
    locked: 'Locked',
    revoked: 'Signed out: {date}',
    revoke: 'Sign out',
    revokeTitle: 'Sign out “{name}”?',
    revokeBody:
      'It leaves at once: its next request is refused, and using it again means enrolling it with e-mail and password.',
    revokeSelf: 'This is the device you are using: you will be signed out too.',
    confirm: 'Sign out',
    cancel: 'Back',
    done: '“{name}” has been signed out.',
    empty: 'No devices registered.',
    emptyHint: 'A device appears here once someone registers it with an e-mail and password.',
  },

  people: {
    title: 'People',
    intro:
      'Who uses the app, and in what role. A new account starts with a one-time password, handed over in person.',
    add: 'New person',
    activeTitle: 'Active',
    disabledTitle: 'Deactivated',
    you: 'You',
    disabled: 'Account deactivated',
    disabledOn: 'Deactivated on {date}',
    passwordToChoose: 'Password to choose',
    passwordExpired: 'Password expired',
    temporaryUntil: 'One-time password, valid until {date}',
    expiredOn: 'The one-time password expired on {date}',
    lastUnlock: 'Last sign-in: {date}',
    neverUnlocked: 'Never signed in',
  },

  newPerson: {
    title: 'New person',
    back: 'People',
    intro:
      'The account starts with a password valid for 72 hours, handed over in person and never by e-mail: at the first sign-in the person chooses their own.',
    name: 'Name',
    nameHint: 'As it shows on the lock screen, for example “Giulia R.”.',
    email: 'E-mail',
    emailHint: 'Used to sign in: one per person.',
    role: 'Role',
    codeRole:
      'This role also signs in with a code: they get the key to add to their authenticator app.',
    locale: 'E-mail language',
    localeHint:
      'Work alerts and summaries arrive in this language; the app changes language from the menu.',
    code: 'Your authenticator app code',
    submit: 'Create the account',
    why: {
      name: 'Type the name.',
      email: 'Type a valid e-mail.',
      role: 'Choose the role.',
      code: 'Type the 6 digits of your code.',
    },
    done: 'Account created for {name}.',
  },

  person: {
    title: 'Person',
    back: 'People',
    notFound: 'This person does not exist.',
    details: 'Details',
    edit: 'Edit',
    name: 'Name',
    nameHint: 'As it shows on the lock screen.',
    email: 'E-mail',
    role: 'Role',
    locale: 'E-mail language',
    localeHint:
      'Work alerts and summaries arrive in this language; the app changes language from the menu.',
    authenticator: 'Authenticator app',
    authenticatorSet: 'Set up',
    authenticatorNone: 'Not needed for this role',
    lastUnlock: 'Last sign-in',
    never: 'None yet',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Changes saved.',
    conflict:
      'Another Owner changed this person meanwhile: nothing was saved. The details above are as they are now; edit again if needed.',
    toCodeRole:
      'This role also signs in with a code: they get a key for their authenticator app, shown once.',
    fromCodeRole: 'This role signs in without a code: their key stops working.',
    roleSelf: 'Another Owner changes your role.',
    roleDisabled: 'The role changes after the account is reactivated.',
    code: 'Your authenticator app code',
    why: {
      name: 'Type the name.',
      code: 'Type the 6 digits of your code.',
      reason: 'Type a reason of {min} characters or more.',
      nothing: 'Nothing has changed.',
    },
    access: 'Access',
    accessIntro:
      'Giving access asks for your code; taking it away does not, so a deactivation takes effect at once.',
    selfAccess:
      'On your own account: you change your password from “Change password”, and another Owner changes your role and access.',
    newPassword: 'New password',
    newKey: 'New authenticator key',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    passwordTitle: 'New password for {name}?',
    passwordBody:
      'The current password stops working at once, and if they are working the app stops until they choose their own. The new one works once, for 72 hours: hand it over in person.',
    passwordConfirm: 'Create the password',
    passwordDone: 'New password created for {name}.',
    keyTitle: 'New authenticator key for {name}?',
    keyBody:
      'For a lost or changed phone: the current key stops working at once, and the new one goes into the authenticator app.',
    keyConfirm: 'Create the key',
    keyDone: 'New key created for {name}.',
    deactivateTitle: 'Deactivate {name}’s account?',
    deactivateBody:
      'They leave every device at once: their sessions end now. They sign in again only after an Owner reactivates the account with their code.',
    reason: 'Reason',
    reasonHint: 'Kept in the log, for example “end of season”.',
    signOut: 'Also sign out these devices',
    signOutHint:
      'Ticked already: those only this person used, such as their phone. A device signed out has to be enrolled again.',
    deactivateConfirm: 'Deactivate',
    keep: 'Back',
    deactivateDone: '{name}’s account deactivated: their sessions have ended.',
    reactivateTitle: 'Reactivate {name}’s account?',
    reactivateBody:
      'They sign in with their password and PIN, as before. Devices that were signed out have to be enrolled again.',
    reactivateExpired: 'Their one-time password has expired: create a new one afterwards.',
    reactivateConfirm: 'Reactivate',
    reactivateDone: '{name}’s account reactivated.',
    devices: 'Devices used',
    devicesNone: 'No device used yet.',
    deviceCurrent: 'This device',
    deviceOnly: 'Only this person',
    deviceRevoked: 'Signed out on {date}',
    deviceLastUsed: 'Last used: {date}',
    deviceNeverUnlocked: 'Enrolled, never unlocked',
  },

  shownOnce: {
    title: 'To hand over in person',
    intro:
      'Shown only now: once you leave this page it cannot be read again. If it is lost, create a new one from the person’s page.',
    password: 'One-time password',
    passwordUntil: 'Valid until {date}. At the first sign-in {name} chooses their own.',
    key: 'Authenticator app key',
    keyHint:
      'In the app (Google Authenticator, Microsoft Authenticator…) it is added as a “setup key”, with or without the spaces.',
    link: 'Open in the authenticator app',
    linkHint: 'On the phone with the app: the link adds it by itself.',
    copy: 'Copy',
    copied: 'Copied.',
    copyFailed: 'Copy did not work: select the text and copy it by hand.',
    done: 'Done',
  },

  newBooking: {
    title: 'New booking',
    cancel: 'Cancel',
    discardTitle: 'Discard the unsaved booking?',
    discardBody: 'What you typed will be lost.',
    discardConfirm: 'Discard',
    discardKeep: 'Keep filling it in',
    source: 'Channel',
    when: 'When',
    pickupDate: 'Pick-up date',
    pickupTime: 'Pick-up time',
    returnDate: 'Return date',
    returnTime: 'Return time',
    pickupPlace: 'Pick-up place',
    returnPlace: 'Return place',
    // S9 (RES-6): a delivery to a port meets the customer's ferry.
    ferryName: 'The customer’s ferry',
    ferryNameHint: 'The one they arrive at the port on, for whoever brings them the vehicle.',
    ferryTime: 'Ferry arrival',
    ferryTimeHint: 'The time, on the day of the pick-up.',
    needFerry: 'Type the ferry and its arrival time, or leave both empty.',
    locations: 'Locations',
    zones: 'Deliveries',
    vehicleSection: 'Vehicle',
    category: 'Category',
    chooseCategory: 'Choose a category',
    vehicle: 'Vehicle',
    anyVehicle: 'Any free vehicle (assigned later)',
    taken: 'taken',
    noRoom: 'No vehicle of this category is free for these dates.',
    extras: 'Extras',
    cover: 'Cover',
    promoCode: 'Promo code',
    priceSection: 'Price',
    quoting: 'Pricing…',
    notBookable: 'Cannot be booked:',
    counterOnlyTitle: 'Office only, the website does not take it:',
    customerSection: 'Customer',
    existingCustomer: 'Existing customer',
    newCustomer: 'New customer',
    searchCustomer: 'Find a customer',
    searchCustomerHint: 'Name, e-mail or phone.',
    noCustomerFound: 'No customer found.',
    // S8: a record whose owner asked to be erased takes no new booking.
    erasureRequested: 'Asked on {date} for their data to be erased: book them as a new customer.',
    choose: 'Choose',
    chosen: 'Customer: {name}',
    change: 'Change',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'E-mail',
    phone: 'Phone',
    phoneHint: 'With the country code, for example +39 333 1234567.',
    birthDate: 'Date of birth',
    nationality: 'Nationality (code, e.g. IT)',
    language: 'Language of the customer’s e-mails',
    optional: 'optional',
    contactNeeded:
      'A phone, WhatsApp or e-mail booking needs at least a phone number or an e-mail.',
    licenceSection: 'Driving licence (optional now, required at pick-up)',
    licenceNumber: 'Licence number',
    licenceCountry: 'Issuing country (code, e.g. IT)',
    licenceCategory: 'Category (e.g. B)',
    licenceFirstIssued: 'Category first issued on',
    licenceExpires: 'Expires on',
    hasIdp: 'Has an International Driving Permit',
    overrideSection: 'Manual price',
    override: 'Charge a price other than the list price',
    overrideTotal: 'Total (€)',
    overrideReason: 'Reason',
    overrideReasonHint: 'Required: it stays in the change log.',
    notes: 'Notes',
    submit: 'Book',
    priceChanged: 'The price has changed: check the new total and confirm again.',
    incomplete: 'Fill in the dates, places and category to see the price.',
    priceDetails: 'Price breakdown',
    needWhen: 'Choose the pick-up and return dates and times.',
    needPlace: 'Choose the pick-up place.',
    needCategory: 'Choose the category.',
    noQuote: 'The price did not come.',
    needCustomer: 'Choose the customer.',
    needName: 'Type the customer’s first and last name.',
    needOverrideTotal: 'Type the manual total.',
    needOverrideReason: 'Enter the reason for the manual price, at least 3 characters.',
    vehiclesWithPrice: 'The free vehicles come with the price.',
    placesFailed: 'The places did not load.',
    optionsFailed: 'The extras and covers did not load.',
    searchTooShort: 'Type at least 2 letters.',
    notesLabel: 'Notes on the booking',
    promoRefused: 'Discount code {code}: {why}',
    promoReasons: {
      unknown: 'it does not exist.',
      inactive: 'it is not active.',
      not_yet_valid: 'it is not valid yet.',
      expired: 'it has expired.',
      exhausted: 'it has been used up.',
      blackout: 'it does not apply on these dates.',
      location: 'it applies only to some pick-up and return places.',
    },
    // S9 (ADM-4): a future booking made on the old site, carried over by hand.
    oldSite: {
      section: 'Booking from the old site',
      reference: 'Reference on the old site',
      referenceHint: 'The code the customer knows, as in the old site’s confirmation e-mail.',
      agreedTotal: 'Agreed price (€)',
      agreedTotalHint: 'The total accepted on the old site: the booking costs this.',
      paid: 'Paid on the old site (€)',
      paidHint: 'Empty if the customer paid nothing.',
      stripeId: 'Payment id in Stripe',
      stripeIdHint:
        'It starts with pi_. With the id the amount is checked with Stripe; without it, it stays as typed and cannot be corrected.',
      noEmail:
        'No e-mail goes to the customer: write to them yourself, quoting the old site’s reference.',
      language: 'Customer’s language (for the contract)',
      needReference: 'Type the old site’s reference.',
      needAgreedTotal: 'Type the agreed price.',
      needPaid: 'Type what was paid on the old site, or leave it empty.',
      needStripeId: 'The Stripe payment id starts with pi_.',
      needPaidForStripe: 'Also type what was paid through Stripe.',
      contactNeeded: 'A phone or an e-mail is needed: the customer is told by hand.',
      listPrice: 'List price today',
      agreed: 'Agreed price',
      paidThere: 'Paid on the old site',
      due: 'To pay at pick-up',
      credit: 'To give back to the customer',
      waivedTitle: 'Old site rules, not checked here:',
      submit: 'Record',
      confirmTitle: 'Record booking {reference} from the old site?',
      confirmLabel: 'Record the booking',
      correct: 'Correct',
      paidWith: '{amount}, through Stripe ({id})',
      paidWithout: '{amount}, without a Stripe id',
      nothingPaid: 'Nothing',
      overpaid: 'More was paid than agreed: check the amounts before recording.',
      noCorrection: 'Without a Stripe id the amount paid cannot be corrected.',
      openTaken: 'Open booking {code}',
    },
  },

  legs: {
    pickup: 'Pick-up',
    return: 'Return',
  },

  refusals: {
    invalid_period: 'The return must be after the pick-up.',
    in_the_past: 'The pick-up was more than 30 minutes ago.',
    category_unavailable: 'This category cannot be booked.',
    season_not_configured: 'Prices are missing from {from} to {to}.',
    rate_not_configured: 'No price is set for this length.',
    day_price_not_configured: 'No calendar price from {from} to {to}.',
    stop_sale: 'Online sale closed from {from} to {to}.',
    min_stay: 'Minimum rental on these dates: {minDays} days.',
    place_unknown: 'This place is no longer available.',
    outside_opening_hours: '{leg} at {time}: {location} is closed.',
    delivery_blackout: '{leg} · {zone}: deliveries are suspended in this period.',
    delivery_lead_time: '{leg} · {zone}: at least {minutes} minutes’ notice.',
    delivery_cutoff: '{leg} · {zone}: book by {cutoff} the day before.',
    delivery_fee_not_configured: '{leg} · {zone}: the delivery fee is not configured.',
    one_way_fee_not_configured: 'The fee for returning elsewhere is not configured.',
    extra_unknown: 'An extra is no longer available.',
    extra_not_for_category: '{extra}: not offered for this category.',
    extra_quantity: '{extra}: at most {max}.',
    extra_out_of_stock: '{extra}: {left} left for these dates.',
    coverage_unknown: 'A cover option is no longer available.',
    driver_too_young: 'Minimum driver age: {minAge}.',
    young_driver_surcharge_not_configured:
      'The young-driver surcharge is not configured for this category.',
  },

  refusalsOneDay: {
    season_not_configured: 'Prices are missing for {day}.',
    day_price_not_configured: 'No calendar price for {day}.',
    stop_sale: 'Online sale closed for {day}.',
  },

  timeline: {
    title: 'Timeline',
    from: 'From',
    days: 'Days',
    earlier: 'Earlier',
    later: 'Later',
    today: 'Today',
    category: 'Category',
    allCategories: 'All categories',
    unassigned: 'Not assigned',
    noVehicles: 'No active vehicle.',
    noCategories: 'No categories.',
    categoryGone: 'Category not found',
    categoryGoneHint: 'The link leads to a category that is no longer there: choose another above.',
    legend: 'Key',
    legendConfirmed: 'Booking',
    legendOut: 'On rent',
    legendUnassigned: 'No vehicle yet',
    legendTurnaround: 'Cleaning',
    legendOutOfService: 'Maintenance or block',
    untilFurtherNotice: 'until further notice',
    bar: '{code}, {name}: {status}, {from} – {to}',
    blockBar: '{kind}: {reason}, {from} – {to}',
    turnaroundBar: 'Cleaning: {from} – {to}',
    now: 'Now',
    dragMoveHint: 'Release to move, Esc to cancel',
    createHint: 'Release to start a booking for these dates, Esc to cancel',
    resizeHint: 'Release to set the new time, Esc to cancel',
    confirmMoveTitle: 'Move this booking?',
    confirmMoveBody: 'Booking {code}. Vehicle: {vehicle}. Dates: {from} → {to}.',
    reasonVehicleTaken: 'That vehicle is already taken for those dates.',
    reasonCategory: 'No vehicle of this category is free for those dates.',
    reasonNotReschedulable: 'This booking can no longer be moved.',
    /** S9: a trip to or from a delivery zone moved where the zone does not take it. */
    reasonDeliveryZone:
      'The delivery zone does not take these dates: deliveries suspended, too little notice, or that day closed.',
    /** Q-151: a hand-over or a return moved outside its pick-up point's hours. */
    reasonOpeningHours:
      'The pick-up point is closed at that time: choose a time within its opening hours.',
    confirmMove: 'Move',
    cancelMove: 'Keep as is',
    moved: 'Booking moved.',
    moveFailed: 'The booking could not be moved. Nothing was changed.',
    /** OPS-1: any number of days, a quarter at most, typed beside the presets. */
    custom: 'Custom…',
    customDays: 'Number of days',
    customRange: '1 to {max} days.',
  },

  prices: {
    title: 'Prices',
    intro: 'Price, minimum days and online sale per category and day. Tap a day to change it.',
    from: 'From',
    category: 'Category',
    allCategories: 'All categories',
    noCategories: 'No categories.',
    categoryGone: 'Category not found',
    categoryGoneHint: 'The link leads to a category that no longer exists: choose another above.',
    activeUnits: { one: '{count} active vehicle', other: '{count} active vehicles' },
    noPrice: '—',
    noPriceSaid: 'no price',
    minPickup: 'min {count} (from the day)',
    minThrough: 'min {count} (whole rental)',
    minPickupSaid: {
      one: 'minimum {count} day for a rental starting here',
      other: 'minimum {count} days for a rental starting here',
    },
    minThroughSaid: {
      one: 'minimum {count} day for any rental that includes this day',
      other: 'minimum {count} days for any rental that includes this day',
    },
    stopSale: 'Online sale closed',
    stopSaleOpen: 'Online sale open',
    free: '{free}/{total} free',
    freeSaid: '{free} free of {total}',
    freeUnknown: '?/{total}',
    noBoundary: 'no start time',
    noBoundarySaid: 'free of {total} not known: the rental day’s start time is not set',
    notCounted: 'not counted',
    notCountedSaid: 'free of {total} not known: the count did not finish',
    cell: '{category}, {date}: {details}',
    pastCell: '{category}, {date}, past day: {details}',
    earlier: 'Earlier',
    later: 'Later',
    today: 'Today',
    dayTitle: '{category}, {date}',
    price: 'Price of the day (€)',
    priceHint: 'For example 45.50. Empty: no price.',
    priceInvalid: 'Type an amount from 0.01 to {max}, for example 45.50.',
    minPickupLabel: 'Minimum days (from the day)',
    minPickupHint: 'For a rental that starts on this day.',
    minThroughLabel: 'Minimum days (whole rental)',
    minThroughHint: 'For any rental that includes this day.',
    minInvalid: 'Type a whole number from 1 to {max}.',
    optional: 'optional',
    stopSaleLabel: 'Close the online sale',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    saved: 'Saved: {category}, {date}.',
    conflict:
      'Someone changed this day while you were editing it: nothing was saved. Here is how it is now and what you typed.',
    conflictTable: 'The day as it is now and what you typed',
    conflictWhat: 'Item',
    conflictNow: 'Now',
    conflictTyped: 'You typed',
    saveOver: 'Save over it',
    none: 'none',
    open: 'open',
    closed: 'closed',
    bulk: 'Change many days',
    categories: 'Categories',
    bulkFrom: 'From',
    bulkTo: 'To',
    fromEarlier: 'Move the first day back',
    fromLater: 'Move the first day on',
    fromToday: 'From today',
    toEarlier: 'Move the last day back',
    toLater: 'Move the last day on',
    toToday: 'Until today',
    weekdays: 'Days of the week',
    allWeek: 'All',
    weekend: 'Weekend',
    whatToSet: 'What to change',
    priceMode: 'Price',
    leave: 'Leave as is',
    setTo: 'Set to',
    upBy: 'Raise by',
    downBy: 'Lower by',
    amount: 'Amount (€)',
    clear: 'Remove',
    minDays: 'Days',
    stopSaleMode: 'Online sale',
    stopOn: 'Close',
    stopOff: 'Open',
    preview: 'Preview',
    why: {
      categories: 'Choose at least one category.',
      weekdays: 'Choose at least one day of the week.',
      range: 'The last day comes before the first.',
      past: 'The first day has already passed.',
      nothing: 'Choose at least one thing to change.',
      amount: 'Type an amount from 0.01 to {max}.',
      min: 'Type the minimum days, from 1 to {max}.',
      code: 'Type the Owner’s code.',
    },
    changesCount: { one: '{count} day changes.', other: '{count} days change.' },
    unchangedCount: { one: '{count} day is already so.', other: '{count} days are already so.' },
    skippedCount: {
      one: '{count} day skipped: it has no price to raise or lower.',
      other: '{count} days skipped: they have no price to raise or lower.',
    },
    changesTable: 'Days that change',
    skippedTable: 'Days skipped',
    colCategory: 'Category',
    colDay: 'Day',
    colBefore: 'Before',
    colAfter: 'After',
    colNote: 'Note',
    colReason: 'Why',
    noPriceToMove: 'No price to raise or lower',
    bigMove: 'Changes by more than half',
    bigMoveCount: {
      one: '{count} price changes by more than half: check it.',
      other: '{count} prices change by more than half: check them.',
    },
    noChanges: 'No day changes.',
    code: 'Owner’s code',
    apply: 'Apply',
    confirmBig: 'Some prices change by more than half. Apply anyway?',
    confirmApply: 'Yes, apply',
    backToPreview: 'Back to the preview',
    stale: 'Someone changed one of these days after the preview: nothing was changed.',
    previewAgain: 'Preview again',
    applied: 'Days changed: {changed}. Already so: {unchanged}. Skipped: {skipped}.',
  },

  vehicles: {
    title: 'Vehicles',
    search: 'Search',
    searchHint: 'Plate, make or model.',
    category: 'Category',
    allCategories: 'All categories',
    retired: 'Show retired vehicles',
    none: 'No vehicle.',
    fills: 'Vehicles come with the company’s configuration.',
    found: {
      one: '{count} vehicle',
      other: '{count} vehicles',
    },
    categoriesFailed: 'The categories did not load.',
    next: 'Next: {code}, {date}',
    noNext: 'No booking coming',
    expiry: '{kind} expires on {date}',
    /** UI-2g: a day already gone, one sentence per kind so the word agrees with it. */
    expired: {
      insurance: 'Insurance expired on {date}',
      revisione: 'Inspection (revisione) expired on {date}',
      bollo: 'Road tax (bollo) expired on {date}',
      other: 'Other document expired on {date}',
    },
  },

  vehicle: {
    back: 'Vehicles',
    title: 'Vehicle',
    notFound: 'No vehicle at this address.',
    details: 'Vehicle details',
    category: 'Category',
    year: 'Year',
    fuel: 'Fuel',
    transmission: 'Gearbox',
    seats: 'Seats',
    odometer: 'Kilometres',
    vin: 'Chassis number',
    notes: 'Notes',
    notGiven: 'Not given',
    blocks: 'Maintenance and blocks',
    noBlocks: 'No maintenance or block.',
    newBlock: 'Take the vehicle out of availability',
    kind: 'Type',
    startDate: 'From day',
    startTime: 'From time',
    endDate: 'To day',
    endTime: 'To time',
    openEnded: 'Leave the end empty: until further notice.',
    reason: 'Reason',
    addBlock: 'Block the vehicle',
    blocked: 'Vehicle blocked.',
    endBlock: 'End now',
    cancelBlock: 'Cancel the block',
    blockEnded: 'Block ended.',
    blockCancelled: 'Block cancelled.',
    endBlockLabel: 'End now: {kind} from {date}',
    cancelBlockLabel: 'Cancel the block: {kind} from {date}',
    cancelBlockTitle: 'Cancel the block?',
    cancelBlockBody: '{kind} from {date}: the vehicle is available again in that period.',
    keep: 'Keep',
    needReason: 'Enter the reason, at least 3 characters.',
    needStart: 'Choose the start day and time.',
    needEndTime: 'Choose the end time, or leave the end day empty.',
    busy: 'The vehicle is booked in that period: {codes}.',
    cancelled: 'Cancelled',
    ended: 'Ended',
    documents: 'Documents',
    noDocuments: 'No document recorded.',
    newDocument: 'Record a document or a renewal',
    documentKind: 'Document',
    expiresOn: 'Expires on',
    reference: 'Reference (optional)',
    addDocument: 'Record',
    documentAdded: 'Document recorded.',
    superseded: 'Superseded',
    expired: 'Expired',
    expiresOnDate: 'Expires on {date}',
    expiredOn: 'Expired on {date}',
    needExpiry: 'Choose the expiry date.',
    bookings: 'Bookings',
    noBookings: 'No booking in the last 30 days or later.',
    damages: 'Damage to repair',
    noDamages: 'No damage to repair.',
    damagesHint: 'It stays on every hand-over’s diagram until it is marked repaired.',
    damageFound: {
      checkout: 'Marked at the hand-over of {code}, on {date}.',
      return: 'Marked at the return of {code}, on {date}.',
    },
    repair: 'Mark as repaired',
    repairLabel: 'Mark as repaired: {description}',
    repaired: 'Repaired: {description}.',
    correctDocument: 'Correct',
    correctDocumentLabel: 'Correct: {kind} expiring {date}',
    correctDocumentTitle: 'Cancel this document?',
    correctDocumentBody:
      '{kind} expiring {date}: it stays in the list, struck through, and no longer counts for the expiries. Then record the right one.',
    correctDocumentConfirm: 'Cancel the document',
    correctReason: 'Reason',
    correctWhy: 'Write the reason, at least {min} characters.',
    voided: 'Cancelled',
    voidedWhy: 'Cancelled: {reason}',
    documentVoided: 'Document cancelled.',
  },

  vehicleStates: {
    available: 'Free',
    reserved: 'Booked now',
    on_rent: 'On rent',
    turnaround: 'Being cleaned',
    maintenance: 'In maintenance',
    blocked: 'Blocked',
    retired: 'Retired',
  },

  maintenanceKinds: {
    service: 'Service',
    repair: 'Repair',
    block: 'Block',
  },

  /** Maintenance: the register of service and repair interventions (with their cost). */
  maintenance: {
    title: 'Maintenance',
    nav: 'Maintenance',
    empty: 'No interventions recorded.',
    columns: {
      date: 'Date',
      vehicle: 'Vehicle',
      kind: 'Type',
      description: 'Description',
      cost: 'Cost',
      supplier: 'Supplier',
      rental: 'Linked rental',
    },
    kinds: {
      service: 'Service',
      repair: 'Repair',
    },
    filters: {
      vehicle: 'Vehicle',
      allVehicles: 'All vehicles',
      from: 'From',
      to: 'To',
      kind: 'Type',
      allKinds: 'All types',
    },
    form: {
      title: 'New intervention',
      open: 'New intervention',
      date: 'Date performed',
      vehicle: 'Vehicle',
      kind: 'Type',
      description: 'Description',
      cost: 'Cost',
      supplier: 'Supplier',
      rental: 'Linked rental (optional)',
      save: 'Save intervention',
      cancel: 'Cancel',
    },
    totals: {
      filtered: 'Total cost (current filters)',
      vehicle: 'Vehicle total cost, all time',
    },
    void: {
      action: 'Void intervention',
      label: 'Void intervention: {description}',
      title: 'Void the intervention of {date}?',
      details: '{vehicle}: {description}',
      confirm: 'Void this intervention? It will no longer count towards costs.',
      reason: 'Reason',
      why: 'Write the reason, at least {min} characters.',
      confirmAction: 'Yes, void',
      keep: 'Keep',
      done: 'Intervention voided.',
    },
  },

  fuels: {
    petrol: 'Petrol',
    diesel: 'Diesel',
    electric: 'Electric',
    hybrid: 'Hybrid',
    lpg: 'LPG',
    methane: 'Methane',
  },

  transmissions: {
    manual: 'Manual',
    automatic: 'Automatic',
  },

  customers: {
    title: 'Customers',
    search: 'Search',
    searchHint: 'Name, e-mail or phone.',
    none: 'No customer found.',
    noneYet: 'No customers yet.',
    fills: 'Each booking brings its customer.',
    found: {
      one: '{count} customer',
      other: '{count} customers',
    },
    bookings: {
      one: '{count} booking',
      other: '{count} bookings',
    },
    last: 'Last: {code}, {date}',
  },

  customer: {
    back: 'Customers',
    title: 'Customer',
    notFound: 'No customer at this address.',
    contacts: 'Details',
    address: 'Address',
    birthPlace: 'Place of birth',
    language: 'Language',
    since: 'Customer since {date}',
    licences: 'Driving licences',
    noLicences: 'No licence recorded.',
    licence: '{number}, {country}',
    categoriesHeld: 'Categories: {categories}',
    expires: 'Expires on {date}',
    expired: 'Expired',
    expiredOn: 'Expired on {date}',
    idp: 'International Driving Permit',
    history: 'Rentals',
    noBookings: 'No booking.',
    // S8: a record whose owner asked to be erased, a contact erased, a customer anonymised.
    erasedOn: 'Erased on {date}',
    contactErased: 'Contact details erased',
    erasureRequested: 'Erasure requested',
    requestNotice:
      'They asked for their data to be erased on {date}: this record takes no more bookings. If they come back to rent, book them as a new customer.',
    anonymisedTitle: 'Anonymised customer',
    anonymisedMeta: 'Customer since {since} · anonymised on {date}',
    remains: {
      title: 'What remains',
      erased:
        'Name, contact details, birth, address, licences and document photos were erased on {date}.',
      keptByLaw:
        'The bookings with their payments, the signed contracts, the fine letters and the fiscal documents remain, as the law requires them to be kept: no erasure date is set for them.',
      ownerOnly:
        'A contract or a letter naming them opens for the Owner only, and every opening is recorded.',
      vehiclePhotos: {
        one: 'The vehicle photos stay {count} day after each rental ends.',
        other: 'The vehicle photos stay {count} days after each rental ends.',
      },
      vehiclePhotosKept: 'The vehicle photos stay until a period to keep them is set.',
    },
    correction: {
      edit: 'Edit details',
      title: 'Correct the details',
      firstName: 'First name',
      lastName: 'Last name',
      birthDate: 'Date of birth',
      birthPlace: 'Place of birth',
      nationality: 'Nationality (code, e.g. IT)',
      email: 'E-mail',
      phone: 'Phone',
      phoneHint: 'With the international prefix, for example +39 333 1234567.',
      addressLine: 'Address',
      postalCode: 'Postcode',
      city: 'Town',
      province: 'Province (e.g. NA)',
      country: 'Country (code, e.g. IT)',
      preferredLocale: 'Language',
      notes: 'Notes',
      fiscal: 'Billing details',
      partyType: 'Bills as',
      partyTypeNone: 'Not set',
      partyTypes: { person: 'Private individual', company: 'Company' },
      vatNumber: 'VAT number (Partita IVA)',
      taxCode: 'Tax code (Codice fiscale)',
      sdiCode: 'SDI code (Codice destinatario)',
      pec: 'Certified e-mail (PEC)',
      optional: 'optional',
      invalid: 'Not valid: check it.',
      contactErased:
        'E-mail, phone and notes were erased at their request on {date}: they cannot be written back.',
      keepsRecords:
        'Contracts already signed and the details already sent to CaRGOS stay as they were: the correction counts from now on.',
      save: 'Save the corrections',
      cancel: 'Cancel',
      saved: 'Details corrected: {fields}.',
      unchanged: 'Nothing corrected: the details were already so.',
      why: {
        nothing: 'You have not changed anything yet.',
        invalid: 'Check the fields marked.',
        conflict: 'Read the record again before saving.',
      },
      conflict:
        'The record changed since you opened it, and nothing was saved. Read it again: what you typed stays.',
      reread: 'Read the record again',
      rereadKept: 'Record read again. Your corrections stay for: {fields}.',
      rereadNone: 'Record read again: you had not changed anything yet.',
      clashed: 'Someone else had changed {fields} too: check before saving.',
      fields: {
        firstName: 'first name',
        lastName: 'last name',
        birthDate: 'date of birth',
        birthPlace: 'place of birth',
        nationality: 'nationality',
        email: 'e-mail',
        phone: 'phone',
        addressLine: 'address',
        postalCode: 'postcode',
        city: 'town',
        province: 'province',
        country: 'country',
        preferredLocale: 'language',
        notes: 'notes',
        partyType: 'billing type',
        vatNumber: 'VAT number',
        taxCode: 'tax code',
        sdiCode: 'SDI code',
        pec: 'certified e-mail',
      },
    },
    licenceCorrection: {
      edit: 'Edit',
      editLabel: 'Edit licence {number}',
      title: 'Correct the licence',
      number: 'Licence number',
      issuingCountry: 'Country of issue (code, e.g. IT)',
      expiresOn: 'Expiry',
      categories: 'Categories',
      category: 'Category (e.g. B)',
      firstIssuedOn: 'Category first issued',
      addCategory: 'Add a category',
      removeCategory: 'Remove',
      removeCategoryLabel: 'Remove category {code}',
      hasIdp: 'Holds an International Driving Permit',
      saved: 'Licence corrected: {fields}.',
      gone: 'This licence is no longer on file: it was erased with the customer’s data.',
      why: {
        categories: 'Give at least one category, with the day it was first issued if known.',
      },
      fields: {
        number: 'number',
        issuingCountry: 'country of issue',
        expiresOn: 'expiry',
        categories: 'categories',
        hasIdp: 'International Driving Permit',
      },
    },
  },

  /** S8 (GDPR): the Owner's privacy panel on the Cliente page, and the holds Sistema names. */
  privacy: {
    title: 'Privacy',
    intro:
      'What happens to this customer’s data, what keeps it and until when. Requests arrive by hand, and are answered within a month of the day they arrived.',
    anonymised: 'Anonymised on {date}: the record names nobody any more.',
    requested: 'Request to erase arrived on {date} {channel}, recorded by {by} on {at}.',
    withdrawable: 'It can be withdrawn until {until}.',
    hold: 'Legal hold since {date}, placed by {by}: {reason}.',
    parts: {
      contact: 'Contact (e-mail, phone, notes)',
      identity: 'Identity (name, birth, address, licences, documents)',
    },
    verdict: {
      erased: 'Erased on {date}.',
      held: 'Kept {holds}.',
      erasable: 'They go at the next run, within the hour.',
      kept: 'Kept until {date}.',
      notConfigured: 'Kept: no period is set for this data yet.',
    },
    until: '{hold} (until {date})',
    holds: {
      not_ended: 'a booking not closed yet',
      balance: 'a balance to settle',
      deposit: 'a deposit held',
      refund: 'a refund on its way',
      dispute: 'a card dispute',
      payment_page: 'a payment page still open',
      stripe_difference: 'a difference with Stripe to clear',
      fine: 'an open fine',
      cargos: 'a CaRGOS report not answered',
      legal_hold: 'the legal hold',
      request_grace: 'the 24 hours to withdraw the request',
      fine_window: 'the time a fine may still arrive',
      damage: 'a damage not yet repaired',
    },
    /** The same, after "Kept". */
    heldBy: {
      not_ended: 'by a booking not closed yet',
      balance: 'by a balance to settle',
      deposit: 'by a deposit held',
      refund: 'by a refund on its way',
      dispute: 'by a card dispute',
      payment_page: 'by a payment page still open',
      stripe_difference: 'by a difference with Stripe to clear',
      fine: 'by an open fine',
      cargos: 'by a CaRGOS report not answered',
      legal_hold: 'by the legal hold',
      request_grace: 'by the 24 hours to withdraw the request',
      fine_window: 'by the time a fine may still arrive',
      damage: 'by a damage not yet repaired',
    },
    bookings: {
      title: 'Bookings',
      renter: 'renter',
      second_driver: 'second driver',
      held: 'Kept {holds}.',
      free: 'Nothing keeps it.',
    },
    keptByLaw: 'Kept by law, whatever is asked: {contracts}, {letters}, {fiscal}.',
    contracts: {
      one: '{count} signed contract',
      other: '{count} signed contracts',
    },
    letters: { one: '{count} fine letter', other: '{count} fine letters' },
    fiscal: {
      one: '{count} fiscal document',
      other: '{count} fiscal documents',
    },
    photos: {
      one: '{count} document photo still stored: it goes with the identity.',
      other: '{count} document photos still stored: they go with the identity.',
    },
    duplicates: {
      title: 'The same person on other records?',
      intro:
        'These records have the same e-mail or the same phone. A request is recorded on each of the person’s records.',
      email: 'same e-mail',
      phone: 'same phone',
    },
    channels: {
      email: 'by e-mail',
      letter: 'by letter',
      in_person: 'in person',
      phone: 'by phone',
    },
    channelNames: {
      email: 'E-mail',
      letter: 'Letter',
      in_person: 'In person',
      phone: 'Phone',
    },
    reasons: {
      litigation: 'litigation under way',
      authority_request: 'a request from an authority',
      insurance_claim: 'an insurance claim',
      other: 'another reason',
    },
    reasonNames: {
      litigation: 'Litigation',
      authority_request: 'Request from an authority',
      insurance_claim: 'Insurance claim',
      other: 'Other reason',
    },
    code: 'Code from the Owner’s authenticator app',
    cancel: 'Cancel',
    request: {
      action: 'Record a request to erase',
      title: 'Record a request to erase their data?',
      preview: 'What would happen to their data:',
      grace:
        'For 24 hours the request can be withdrawn; then the data goes as soon as nothing keeps it. Signed contracts, fine letters and fiscal documents stay all the same, by law.',
      duplicates: {
        one: 'Another record has the same e-mail or phone: if it is the same person, record the request there too.',
        other:
          '{count} other records have the same e-mail or phone: if they are the same person’s, record the request there too.',
      },
      requestedOn: 'Day it arrived',
      requestedOnHint: 'The month to answer runs from this day.',
      channel: 'How it arrived',
      confirm: 'Record the request',
      why: {
        day: 'Give the day it arrived: today or before.',
        channel: 'Say how it arrived.',
        code: 'Type the code’s six digits.',
      },
      done: 'Request recorded. Nothing goes before {until}: until then it can be withdrawn.',
    },
    withdraw: {
      action: 'Withdraw the request',
      title: 'Withdraw the request to erase?',
      body: 'The request that arrived on {date} is withdrawn, as if it had never been recorded. This can be done until {until}.',
      confirm: 'Withdraw the request',
      keep: 'Keep the request',
      done: 'Request withdrawn: the data follows the retention rules again.',
    },
    legalHold: {
      action: 'Place a legal hold',
      title: 'Place a legal hold?',
      body: 'While it stands, nothing of this customer’s data is erased, not even at their request. Lifting it takes the Owner’s code.',
      reason: 'Reason',
      confirm: 'Place the hold',
      why: 'Choose the reason.',
      done: 'Legal hold placed: nothing of their data is erased while it stands.',
    },
    lift: {
      action: 'Lift the legal hold',
      title: 'Lift the legal hold?',
      body: 'The hold placed on {date} for {reason} is lifted: the data follows the retention rules again.',
      confirm: 'Lift the hold',
      why: 'Type the code’s six digits.',
      done: 'Legal hold lifted.',
    },
    export: {
      action: 'Export their data',
      title: 'Export the customer’s data?',
      body: 'A ZIP archive with the data we keep on the customer: their record, licences, bookings, payments, contracts and fine letters, with a file explaining what it holds, in their language. It holds no signature strokes, no vehicle photos and nothing about staff.',
      purpose: 'What they ask for',
      purposes: {
        access: 'A copy of their data (art. 15)',
        portability: 'Their data, to take elsewhere (art. 20)',
      },
      documents: 'Photos of their ID documents',
      documentsChoices: { include: 'Include them', leave: 'Leave them out' },
      documentsHint: 'The most sensitive part of the file: include them if they asked for them.',
      care: 'The file holds personal data: hand it over as the procedure says, after checking who receives it, then delete it from this device.',
      confirm: 'Prepare the file',
      why: {
        purpose: 'Say what they ask for.',
        documents: 'Choose whether the ID photos go in.',
      },
      done: 'The file is ready and the browser is saving it. It holds personal data: hand it over as the procedure says, then delete it from this device.',
      notInApp: 'Data export in the iPad app: do it from the web app.',
    },
  },

  languages: {
    it: 'Italian',
    en: 'English',
    de: 'German',
  },

  /** W2-S S4: a website booking waiting for its online share. */
  hold: {
    held: 'Waiting for the online payment',
    ended: 'Online payment not completed',
    heldNote:
      'The customer pays {amount} online, their share; the car is held until {time}. Only that payment confirms the booking.',
    endedNote:
      'The online payment was not completed in time: the car is no longer held. If the customer still pays, the booking confirms itself and, if no car is left, Today says so.',
  },

  status: {
    draft: 'Draft',
    confirmed: 'Confirmed',
    checked_out: 'On rent',
    returned: 'Returned',
    closed: 'Closed',
    cancelled: 'Cancelled',
    no_show: 'No-show',
  },

  rental: {
    handOver: 'Hand the vehicle over',
    takeBack: 'Record the return',
    title: 'Rental',
    checkedOut: 'Handed over on {date} by {name}',
    returned: 'Returned on {date} to {name}',
    notReturned: 'Not returned yet.',
    kmOut: 'Km at hand-over',
    kmIn: 'Km at return',
    kmDriven: 'Km driven',
    fuelOut: 'Fuel at hand-over',
    fuelIn: 'Fuel at return',
    contract: 'Paper contract',
    cargos: 'CaRGOS receipt',
    override: 'Handed over with the Owner’s authorisation: {reason}',
    notes: 'Notes at return',
    handedOver: 'Vehicle handed over.',
    takenBack: 'Vehicle returned.',
    fuelFull: 'Full (8/8)',
    fuelEmpty: 'Empty (0/8)',
    fuelEighths: '{level}/8',
  },

  checkout: {
    title: 'Hand-over {code}',
    back: 'Booking',
    vehicle: 'Vehicle that leaves',
    chooseVehicle: 'Choose the vehicle',
    taken: 'taken',
    km: 'Kilometres',
    kmHint: 'Last reading on record: {km} km.',
    fuel: 'Fuel',
    readings: 'Kilometres and fuel',
    checks: 'Checks at the counter',
    documents: 'I have seen the original identity document and driving licence',
    contractTitle: 'Contract',
    contractChooseVehicle: 'Choose the vehicle first: the contract names its plate.',
    contractSigned: 'Signed by the customer on the device on {date}, for this vehicle.',
    contractOtherVehicle:
      'The signed contract names another vehicle: this one needs a new signature.',
    contractStale: 'The booking changed after signing: the contract must be signed again.',
    contractWaiting: 'The customer has not signed yet.',
    turn: 'Turn the device to the customer to sign',
    turnHint:
      'Your session closes and the customer sees only the contract. After signing, unlock again with your PIN.',
    paper: 'Paper contract, if the device cannot be used',
    contract: 'The customer signed the paper contract',
    contractHint: 'Printed form with the terms version {version}.',
    termsMissing: 'No terms are in force: no vehicle can leave. The Owner must load them.',
    termsDraft:
      'The terms in force are a draft: no contract can be signed and no vehicle can leave. The Owner must load the final terms.',
    companyIncomplete:
      'The company’s details for the contract are missing (legal name, address, VAT number): the contract on the device cannot be prepared.',
    paperNeedsOwner:
      'The contract signed on the device is required: with the paper one, the hand-over needs the Owner.',
    contractReference: 'Contract number',
    deposit: 'Security deposit',
    moneyHint: 'Record the payment from the booking, after the hand-over.',
    blocked: 'The hand-over is blocked:',
    mustDo: 'Before the hand-over:',
    mustDoHint: 'No one can hand over without these, not even the Owner.',
    blocks: {
      identity: 'record the driver’s details, with the home address',
      cargos: 'the CaRGOS receipt for this vehicle is missing',
      eligibility: 'the driver does not meet the requirements',
      birth_date: 'the birth date on the document is not the booking’s',
      contract: 'the contract signed on the device for this vehicle is missing',
      deposit: 'no deposit is held',
      second_identity: 'record the second driver’s details',
      second_driver:
        'the second driver does not meet the requirements, or their document shows another birth date',
    },
    ownerOnly: 'Only the Owner can hand over anyway, with a reason.',
    override: 'I authorise the hand-over as the Owner',
    overrideReason: 'Reason',
    overrideReasonHint: 'Required: it stays in the change log.',
    opensAt: 'The vehicle can leave from {time}.',
    submit: 'Hand over',
    railTitle: 'To hand over',
    railDetails: 'What blocks the hand-over, and the account',
    notConfirmed: 'This booking cannot be handed over: only a confirmed booking leaves.',
    why: {
      terms: 'The terms in force do not allow a hand-over: see above.',
      late: 'The booked return time has passed: this booking can no longer leave.',
      vehicle: 'Choose the vehicle that leaves.',
      km: 'Enter the kilometres.',
      fuel: 'Choose the fuel level.',
      reading: 'One moment: reading the photos kept on this iPad.',
      unsent: 'Inspection photos not yet sent from this iPad.',
      unsentReason: 'Enter why it goes ahead without the photos, at least 3 characters.',
      documents: 'Confirm you have seen the original documents.',
      overrideReason: 'Enter the reason for the authorisation, at least 3 characters.',
    },
  },

  secondDriver: {
    title: 'Second driver',
    none: 'No second driver: only whoever booked drives.',
    add: 'Add a second driver',
    replace: 'Change second driver',
    remove: 'Take the second driver off',
    unavailable: {
      not_before_handover: 'Added or taken off only before the hand-over, on a confirmed booking.',
      terms_forbid:
        'The terms in force reserve driving to the signer: a second driver cannot be added.',
      fee_not_configured: 'The second-driver fee is not configured: one cannot be added.',
    },
    who: 'Who drives',
    whoOptions: { existing: 'A customer on file', new: 'A new person' },
    licence: 'Licence',
    licenceHint:
      'Needed to check the requirements; without it they stay to be checked. The original documents are still recorded at the counter.',
    licenceHintExisting:
      'One already on file need not be typed again; a new one is added to their record.',
    noLicence: 'No licence on file',
    expires: 'expires {date}',
    fee: 'Fee',
    feeRow: 'Second driver',
    feeLine: 'Fee {amount}, once per rental: the total becomes {total}.',
    feeFree: 'No fee: the total stays {total}.',
    voids:
      'A contract or a CaRGOS report made before no longer counts: each is made again with the second driver.',
    identity: 'Details for the hand-over',
    identityRecorded: 'recorded',
    identityMissing: 'to record at the hand-over',
    ineligible: 'They do not meet this category’s requirements:',
    ownerMay: 'As the Owner you may let them drive anyway, with a reason.',
    ownerOnly: 'Only the Owner can let them drive anyway.',
    overrideReason: 'Owner’s reason',
    overrideReasonHint: 'Required: it stays on the booking and in the change log.',
    overridden: 'Authorised by the Owner: {reason}',
    addedBy: 'Added by {name}, {date}.',
    save: 'Add',
    saveReplace: 'Change',
    cancel: 'Cancel',
    added: 'Second driver added.',
    replaced: 'Second driver changed.',
    unchanged: 'They were already the second driver: nothing changed.',
    removed: 'Second driver taken off: the total is back to {total}.',
    removeTitle: 'Take the second driver off?',
    removeBody:
      '{name} will no longer drive: the {amount} fee leaves the total. A contract or a CaRGOS report made before is made again.',
    removeConfirm: 'Take off',
    why: {
      customer: 'Choose the customer.',
      firstName: 'Enter the first name.',
      lastName: 'Enter the last name.',
      nationality: 'Enter the nationality as two letters, or leave it empty.',
      phone: 'Correct the phone, or leave it empty.',
      licenceNumber: 'Enter the whole licence number, at least 3 characters.',
      licenceCountry: 'Enter the licence’s issuing country as two letters.',
      overrideReason: 'Enter the reason, at least 3 characters.',
    },
  },

  identity: {
    title: 'Driver’s details',
    secondTitle: 'Second driver’s details: {name}',
    secondIntro: 'As on the second driver’s original documents. Needed before the hand-over.',
    secondIntroCargos:
      'As on the second driver’s original documents. Needed before the hand-over and, for this vehicle, by CaRGOS: the phone too.',
    secondBirthDateDiffers:
      'When they were added the birth date was {date}: if the document says otherwise, the hand-over needs the Owner.',
    secondPhoneHint:
      'Required: CaRGOS asks for the second driver’s. With the international prefix.',
    intro:
      'As on the original documents. Every hand-over needs them; the home address also goes on the contract.',
    introCargos:
      'As on the original documents. Every hand-over needs them and, for this vehicle, so does CaRGOS: the home address also goes on the contract.',
    recordedBy: 'Recorded by {name}, {date}.',
    edit: 'Correct the details',
    editAfterReceipt:
      'CaRGOS already has the receipt for these details: correcting them needs a new report, with the document’s number and place of issue typed again.',
    cancel: 'Cancel',
    surname: 'Surname',
    givenNames: 'Given names',
    namesHint: 'As printed on the document.',
    birthDate: 'Date of birth',
    birthDateDiffers:
      'The booking has {date} as the date of birth: if the document says otherwise, the hand-over needs the Owner.',
    birthPlace: 'Place of birth',
    citizenship: 'Citizenship',
    residence: 'Home address',
    address: 'Street and number',
    postalCode: 'Postal code',
    noPostalCode: 'The country has no postal codes',
    city: 'Town',
    country: 'Country',
    residencePlace: 'Comune of residence, or the state if abroad',
    addressSource: 'The address is',
    sources: {
      document: 'printed on the document',
      declared: 'declared by the customer (the document does not show it)',
    },
    document: 'Identity document',
    documentType: 'Type',
    documentNumber: 'Number',
    documentNumberHint: 'Never prefilled: it is erased by itself once CaRGOS has the receipt.',
    documentPlace: 'Issued in',
    licence: 'Driving licence',
    licenceNumber: 'Number',
    licencePlace: 'Issued in',
    phone: 'Phone',
    phoneHint: 'With the international prefix, for example +39 333 1234567.',
    save: 'Record the details',
    why: {
      surname: 'Enter the surname.',
      givenNames: 'Enter the given names.',
      birthDate: 'Enter the date of birth.',
      birthPlace: 'Choose the place of birth.',
      citizenship: 'Choose the citizenship.',
      address: 'Enter the street and number, at least 3 characters.',
      postalCode: 'Enter the postal code, or tick that the country has none.',
      city: 'Enter the town.',
      country: 'Choose the country.',
      residencePlace: 'Choose the comune of residence, or the state if abroad.',
      addressSource:
        'Say whether the address is printed on the document or declared by the customer.',
      documentType: 'Choose the type of identity document.',
      documentNumber: 'Enter the identity document’s number.',
      documentPlace: 'Choose where the identity document was issued.',
      licenceNumber: 'Enter the driving licence’s number.',
      licencePlace: 'Choose where the driving licence was issued.',
      phone: 'Correct the phone, or leave it empty.',
      phoneRequired: 'Enter the second driver’s phone: CaRGOS asks for it.',
    },
    saved: 'Details recorded.',
    unchanged: 'These details were already recorded.',
    erased: 'number and place of issue erased on {date}',
    problems: 'CaRGOS would refuse these details: correct them.',
    problem: {
      missing: 'missing',
      too_long: 'too long',
      too_short: 'too short',
      charset: 'contains characters that are not allowed',
      unknown_code: 'not in CaRGOS’s tables',
      place_kind: 'an Italian comune, or a state, is needed',
      dissolved_code: 'no longer exists',
      pair_incomplete: 'incomplete',
      driver2_incomplete: 'incomplete',
    },
    phoneInvalid: 'Enter the number with the international prefix, only digits after the +.',
  },

  places: {
    search: 'Search for a place',
    searchHint: 'At least 2 letters of the name.',
    none: 'No place with this name.',
    searching: 'Searching…',
    found: { one: '{count} place found', other: '{count} places found' },
    change: 'Change',
    gone: 'gone since {date}',
    state: 'state',
  },

  cargos: {
    title: 'CaRGOS',
    notRequired: 'This category is not reported to CaRGOS.',
    pending:
      'Sending from this app waits for its credentials: for now, send the contract from the CaRGOS portal, or by PEC, and record the receipt here.',
    covered: 'Receipt {receipt} for {plate}: the vehicle may leave.',
    cover: {
      none: 'No receipt for this hand-over.',
      other_vehicle: 'The receipt is for another vehicle: this one must be reported again.',
      booking_changed: 'The booking changed after the receipt: it must be reported again.',
      data_changed: 'The driver’s details changed after the receipt: they must be reported again.',
    },
    needIdentity: 'Record the driver’s details first.',
    needIdentityCargos: 'Record the driver’s details again, with those for CaRGOS.',
    needVehicle: 'Choose the vehicle first.',
    sendTitle: 'Report from the app',
    paymentType: 'Payment (CaRGOS table)',
    choosePaymentType: 'Choose how the customer pays',
    why: {
      receipt: 'Enter the receipt number.',
      paymentType: 'Choose how the customer pays.',
      contract:
        'The contract first: signed on the device, or the printed one signed and ticked above.',
      portal: 'Tick that you checked the CaRGOS portal and the report is not there.',
    },
    checkMode:
      'Trial mode: CaRGOS checks the report but does not keep it. For the vehicle to leave, send from the portal and record the receipt here.',
    send: 'Send to CaRGOS',
    sendAgain: 'Send again',
    portalChecked: 'I checked the CaRGOS portal: this report is not there.',
    check: 'Check with CaRGOS (trial)',
    queued: 'Sending to CaRGOS…',
    retrying: 'Try {tries} failed: CaRGOS not reached. Next try at {time}.',
    lastReason: 'Reason: {reason}',
    stuck:
      'Sending has stopped: tell the Owner (System). Meanwhile send from the CaRGOS portal and record the receipt here.',
    checked: 'Trial passed: CaRGOS would accept this report. A trial is not a report.',
    unknown:
      'Outcome unknown: the report may have reached CaRGOS. Check the portal. If it is there, record its receipt below; if not, send again (same contract number).',
    refused: 'CaRGOS refused the report. Correct the details and send again.',
    /** UI-1d (C5): CaRGOS's own description, quoted; its code is for Technical details. */
    refusedSaid: 'The reason, in CaRGOS’s words: “{text}”',
    refusedUnexplained:
      'CaRGOS refused the report without saying why. Check the details and send again; if it is refused again, tell the Owner.',
    refusedRecord:
      'CaRGOS would no longer accept this report: one of the codes it uses is no longer in force. Record the driver’s details again, then send; if it is refused again, tell the Owner.',
    technicalCode: 'Code: {code}',
    cancelled: {
      default: 'Report cancelled: a receipt from the portal or the PEC was recorded.',
      booking_status: 'Report cancelled: the booking is no longer confirmed.',
      booking_changed: 'Report cancelled: the booking changed. Send again.',
      cargos_not_required: 'Report cancelled: CaRGOS no longer applies to this booking.',
      identity_changed: 'Report cancelled: the driver’s details were recorded again. Send again.',
      vehicle_not_in_category: 'Report cancelled: the vehicle is no longer in this category.',
      vehicle_still_out: 'Report cancelled: the vehicle is out with another booking.',
      not_reached:
        'Report cancelled: CaRGOS could not be reached for hours. Send again, or from the portal.',
    },
    receiptTitle: 'Record a receipt (portal or PEC)',
    // S9: on a trip, the portal's or the PEC's receipt is the office's to type.
    officeReceipt:
      'The office records the portal’s or the PEC’s receipt: ask the office for it. It shows here as soon as it is recorded.',
    channel: 'Receipt from',
    channels: { api: 'app', portal: 'CaRGOS portal', pec: 'PEC' },
    receipt: 'Receipt number',
    record: 'Record the receipt',
    recorded: 'Receipt recorded: the document’s number and place of issue have been erased.',
    history: 'Reports',
    entry: '{contract} · {plate} · {status}',
    status: {
      queued: 'Queued',
      checked: 'Checked (trial)',
      sent: 'Sent',
      refused: 'Refused',
      unknown: 'Outcome unknown',
      cancelled: 'Cancelled',
      recorded: 'Recorded',
    },
  },

  documents: {
    none: 'No document on file.',
    fromCustomer: 'sent by the customer',
    atCounter: 'photographed at the counter',
    open: 'Open',
    openLabel: 'Open {document}',
    openHint: 'Every time a document is opened, it is logged.',
    capture: 'Photograph a document',
    kind: 'Document',
    side: 'Side',
    photo: 'Take the photo',
    photoHint:
      'On the device the camera opens. The photo is made smaller and saved without its location.',
    preview: 'Preview: {document}',
    // S8: a document's photo erased (retention, or with its customer's identity).
    erased: 'Photo erased on {date}',
    save: 'Save the photo',
    saving: 'Saving…',
    saved: '{document}: photo saved.',
    unreadable: 'This photo cannot be read: take it again.',
    choosePhoto: 'Take or choose a photo first.',
    pdfNotInApp:
      'It is a PDF, and PDFs cannot open in this app right now: close it and open it again, or open the PDF from a computer.',
  },

  files: {
    close: 'Close',
    photoUnavailable: 'Photo not available',
  },

  inspection: {
    title: 'Vehicle inspection',
    introCheckout:
      'Before it leaves: photograph the vehicle zone by zone and mark the damage already there. It is not charged to the customer.',
    introReturn:
      'At the return: the hand-over photos sit beside today’s. A new damage becomes a charge, for the amount you type.',
    introClosed:
      'The photos from the hand-over and the return, and the damage on file for this vehicle.',
    chooseVehicle: 'Choose the vehicle that leaves first.',
    diagram: 'Diagram of {plate}',
    zones: {
      front: 'Front',
      left: 'Left side',
      right: 'Right side',
      rear: 'Rear',
      roof: 'Roof',
      interior: 'Interior',
      seat: 'Seat',
    },
    photoCount: { one: '{count} photo', other: '{count} photos' },
    damageCount: { one: '{count} damage', other: '{count} damages' },
    zoneLabel: '{zone}: {photos}, {damages}',
    atCheckout: 'At the hand-over',
    atReturn: 'At the return',
    noPhotos: 'No photos.',
    // S8: photos erased since, where they were, and when.
    erasedPhotos: {
      one: '{count} photo erased {when}.',
      other: '{count} photos erased {when}.',
    },
    erasedSummary: {
      one: '{count} photo of this booking was erased {when}.',
      other: '{count} photos of this booking were erased {when}.',
    },
    erasedOnDay: 'on {date}',
    erasedBetween: 'between {from} and {to}',
    photoAlt: '{zone}, {stage}, photo {number}',
    damagePhotoAlt: 'Damage, {zone}: {description}',
    takePhoto: 'Add a photo',
    addPhoto: 'Add a photo: {zone}',
    unreadable: 'This photo cannot be read: take it again.',
    damagesTitle: 'Damage on this vehicle',
    noDamages: 'No damage on file.',
    existing: 'already there',
    newHere: 'new at this return',
    foundOn: 'marked on {date}',
    charged: 'charged {amount}',
    markLabel: 'New damage: {zone}',
    markHintCheckout: 'Describe the damage already there, then photograph it.',
    markHintReturn: 'Describe the new damage, then photograph it: it is added to the charges.',
    mark: 'Mark the damage',
    describe: 'Say what the damage is.',
    damagePhoto: 'Add a photo of the damage: {description}',
    charge: 'Add to the charges',
    chargeLabel: 'Add to the charges: {description}',
    chargesFull: 'There are already 20 charges: remove one to add this damage.',
    inCharges: 'In the charges',
    chargeDescription: '{zone}: {description}',
  },

  /** S4 (OPS-5): the inspection's photos and damages, kept on the iPad until the server has them. */
  capture: {
    photoStates: {
      queued: 'Waiting',
      sending: 'Sending…',
      sent: 'Sent',
      refused: 'Not sent',
    },
    damageStates: {
      queued: 'Waiting',
      sending: 'Sending…',
      sent: 'Sent',
      refused: 'Not sent',
    },
    noConnection: 'No connection: it goes as soon as it is back.',
    serverBusy: 'The server is not answering: it tries again by itself.',
    waitsFor: 'It goes when {name} unlocks the device.',
    waitsForDamage: 'It goes after the damage it shows.',
    retry: 'Try again',
    discard: 'Discard',
    discardTitle: {
      one: 'Discard {count} unsent item?',
      other: 'Discard {count} unsent items?',
    },
    discardBody:
      'It will not reach the server and cannot be recovered. A damage discarded takes its photos with it.',
    keep: 'Keep',
    full: 'The device has no room left: send or discard what is waiting, then try again.',
    notDurable: 'This browser does not keep photos: they last only while the page stays open.',
    limitPhotos: 'This inspection already has the most photos allowed.',
    limitDamages: 'This inspection already has the most damages allowed.',
    saved: 'Photo kept on the device.',
    damageSaved: 'Damage kept on the device: {description}.',
    unsent: {
      one: '{count} inspection item has not reached the server from this iPad yet.',
      other: '{count} inspection items have not reached the server from this iPad yet.',
    },
    waitCheckout: 'The vehicle leaves once they have arrived.',
    waitReturn: 'The return is recorded once they have arrived.',
    theirs: {
      one: '{count} is {name}’s: it goes when {name} unlocks the device.',
      other: '{count} are {name}’s: they go when {name} unlocks the device.',
    },
    discardMine: 'Discard my unsent items',
    ownerProceed: 'Go ahead without them (Owner only)',
    ownerReason: 'Why it goes ahead without them',
    ownerReasonHint: 'Kept in the log, with the number of items left on the device.',
    elsewhere: {
      one: '{count} item of another vehicle on this booking is unsent: it does not hold up the hand-over.',
      other:
        '{count} items of other vehicles on this booking are unsent: they do not hold up the hand-over.',
    },
    reading: 'Reading what is kept on the device…',
    indicator: 'Waiting: {count}',
    /** UI-1e: said with the count when something was refused, never by colour alone. */
    indicatorRefused: { one: '{count} refused', other: '{count} refused' },
    /** What the button does, said after its words (its name is what it shows). */
    indicatorHint: 'Not sent yet from this iPad: open the list.',
    listTitle: 'Waiting on this iPad',
    listEmpty: 'Nothing waiting.',
    photoOf: 'Photo · {zone}',
    damageOf: 'Damage · {zone}: {description}',
    where: '{plate} · {code} · {stage}',
    takenBy: '{name}, {time}',
    stale:
      'Some inspection items have waited more than 15 minutes. Check the connection, or who needs to unlock the device.',
    staleLink: 'See the list',
    close: 'Close',
    kept: {
      one: '{count} inspection item is kept on this iPad: it goes after the unlock.',
      other: '{count} inspection items are kept on this iPad: they go after the unlock.',
    },
    offline: 'No connection: unlocking needs the network.',
  },

  lifecycle: {
    cancel: 'Cancel the booking',
    noShow: 'Customer did not show',
    close: 'Close the booking',
    cancelTitle: 'Cancel booking {code}?',
    noShowTitle: 'Did the customer of {code} not show up?',
    closeTitle: 'Close booking {code}?',
    closeBody:
      'The vehicle is back and the account is settled. A closed booking takes no more payments.',
    back: 'Back',
    confirmCancel: 'Cancel the booking',
    confirmNoShow: 'Confirm: did not show',
    confirmClose: 'Close the booking',
    pickup: 'Booked pick-up: {date}.',
    hoursLeft: {
      one: '{count} hour to the pick-up.',
      other: '{count} hours to the pick-up.',
    },
    underAnHour: 'Less than an hour to the pick-up.',
    pickupPassed: 'The pick-up time has passed.',
    carFreed: 'The vehicle is free for other bookings at once.',
    policyFee: 'Fee set by the terms {version}: {amount}.',
    unconfirmedNoFee:
      'The booking is not confirmed yet (the customer has not paid their share online): it is cancelled with no fee.',
    tier: {
      one: 'For cancellations made at least {count} hour before the pick-up.',
      other: 'For cancellations made at least {count} hours before the pick-up.',
    },
    lastTier: 'For cancellations closest to the pick-up, and after it.',
    notAccepted:
      'The customer did not accept these terms online: the booking was made at the counter or on the phone.',
    feeChoice: 'Fee',
    feePolicy: 'Keep the fee the terms set: {amount}',
    feeOverride: 'Reduce the fee',
    overrideAmount: 'Reduced fee (€)',
    overrideAmountHint: 'Less than {amount}.',
    overrideTooHigh: 'Enter an amount lower than {amount}, for example 20 or 20.50.',
    overrideReason: 'Why it is reduced',
    overrideReasonHint: 'Required: it stays in the change log.',
    noPolicy: 'This booking’s terms say nothing about cancelling: enter what is kept.',
    typedAmount: 'Fee kept (€)',
    typedAmountHint: 'From 0 to {amount}. Enter 0 if nothing is kept.',
    amountInvalid: 'Enter an amount from 0 to {amount}, for example 20 or 20.50.',
    reason: 'Why it is cancelled',
    reasonHint: 'Required: it stays on the booking.',
    reasonShort: 'Enter at least 3 characters.',
    note: 'Notes',
    paidSoFar: 'Paid so far: {amount}.',
    afterOwed: 'Afterwards: {amount} still to pay, at the counter.',
    afterCredit: 'Afterwards: {amount} to give back to the customer.',
    afterSettled: 'Afterwards: the account is settled.',
    blocks: {
      status: 'The booking is no longer confirmed: check where it stands now.',
      too_early: 'A no-show is marked from the pick-up time, {time}.',
      deposit: 'A deposit is still held: release it first, in the Deposit panel.',
    },
    closeBlocked: 'Before the booking can be closed:',
    closeBlocks: {
      status: 'the vehicle must be back',
      balance: 'the account must be settled',
      deposit: 'the deposit must be released or kept',
      refund: 'the refund on its way must finish',
      dispute: 'the dispute by the customer’s bank must end',
    },
    done: {
      cancelled: 'Booking cancelled.',
      no_show: 'Marked as a no-show.',
      closed: 'Booking closed.',
    },
    paidOnlineMeanwhile:
      'The customer had just paid online: the payment is recorded and the account shows what to give back.',
    ended: {
      cancelled: 'Cancelled on {date} by {name}.',
      no_show: 'No-show: marked on {date} by {name}.',
      closed: 'Closed on {date} by {name}.',
    },
    endedAt: {
      cancelled: 'Cancelled on {date}.',
      no_show: 'No-show: marked on {date}.',
      closed: 'Closed on {date}.',
    },
    reasonLine: 'Reason: {reason}',
    /** UI-1e: what the dialog still waits for, under its buttons. */
    why: {
      blocked: 'First what is listed above.',
      fee: 'Enter the fee kept, 0 if nothing is kept.',
      lowerFee: 'Enter the reduced fee.',
      feeReason: 'Enter why it is reduced, at least 3 characters.',
      reason: 'Enter why it is cancelled, at least 3 characters.',
      note: 'Enter at least 3 characters in the notes, or leave them empty.',
    },
  },

  deposit: {
    title: 'Security deposit',
    amount: 'Deposit for this category: {amount}.',
    unset: 'The amount for this category is not decided yet.',
    none: 'This category takes no deposit.',
    required: 'A deposit must be held before the vehicle leaves.',
    readerPending: 'Card reader: take the deposit on the shop’s POS and record it here.',
    readerPendingOrCash:
      'Card reader: take the deposit on the shop’s POS or in cash and record it here.',
    method: 'How the deposit is taken',
    methods: {
      reader: 'Card, on the reader',
      pos: 'Card, on the shop’s POS',
      cash: 'Cash',
    },
    reference: 'POS slip number',
    take: 'Take the deposit of {amount}',
    takeTitle: 'Take the deposit',
    waiting: 'The reader is waiting for the customer’s card ({amount}).',
    simulated: 'Stripe’s simulated reader, in test mode: no real card is charged.',
    simulate: 'Simulate the customer’s card (test)',
    check: 'Check with Stripe',
    stopWaiting: 'Cancel on the reader',
    held: {
      reader: '{amount} held on the {brand} card.',
      pos: '{amount} held on the shop’s POS.',
      cash: 'Cash deposit: {amount}.',
    },
    posReference: 'POS slip: {reference}.',
    captureBefore: 'The hold lapses on {date}.',
    lapses:
      'It lapses before the return on {date}: take it again before it lapses, or take the deposit in cash or on the POS.',
    debit: 'Debit card: the customer’s money stays blocked in their account until it is released.',
    takenBy: 'Taken by {name} on {date}.',
    release: 'Release the deposit',
    releaseCash: 'Give the cash back',
    keepTitle: 'Keep part of the deposit',
    keepAmount: 'Amount to keep (€)',
    keepAmountHint: 'At most {amount}: what is still owed, within the deposit.',
    keepReason: 'Reason',
    keepReasonHint: 'Required: it stays in the change log.',
    keep: 'Keep',
    keepNothing: 'Nothing is owed: release the deposit.',
    keepConfirm: {
      title: 'Keep {amount} of the deposit?',
      payment: 'A payment of {amount} is recorded from the deposit.',
      released: 'The rest, {amount}, is released.',
      cash: 'Give the rest, {amount}, back to the customer in cash.',
      final: 'This cannot be undone.',
      confirm: 'Keep {amount}',
    },
    why: {
      method: 'Choose how the deposit is taken.',
      drawer: 'Cash needs an open drawer.',
      amount: 'Enter the amount to keep.',
      overMax: 'At most {amount}.',
      reason: 'Enter the reason, at least 3 characters.',
      drawerBack: 'Giving the rest back in cash needs an open drawer.',
    },
    ended: {
      released: 'Deposit released on {date}.',
      captured: '{kept} kept on {date}: {reason}. The rest was released.',
      capturedCash: '{kept} kept on {date}: {reason}. Give the customer back {rest}.',
      expired: 'The hold lapsed on {date}: there is no deposit any more.',
      failed: 'The card was not held: {why}. Try again, or choose another way.',
    },
    declined: {
      funds: 'it does not have enough funds',
      expired: 'it has expired',
      pin: 'the reader asked for the PIN, or the PIN was wrong',
      temporary: 'a temporary error at the bank or the reader',
      cancelled: 'the attempt was called off before it went through',
      declined: 'the bank refused it',
    },
    done: {
      taken: 'Deposit recorded.',
      released: 'Deposit released.',
      kept: 'Amount kept from the deposit and recorded as a payment.',
    },
  },

  giveBack: {
    title: 'Return {code}',
    km: 'Kilometres at return',
    kmHint: 'At hand-over: {km} km.',
    fuel: 'Fuel at return',
    fuelHint: 'At hand-over: {level}.',
    /** UI-1d: the server adds it only at a price fixed at the hand-over (rentals.ts). */
    fuelCharge: 'Less fuel than at hand-over: the refuelling fee is added, if the terms set one.',
    excessKmDriven: 'Driven: {km} km',
    excessKmIncluded: 'Included: {km} km',
    excessKmOver: 'Over the allowance: {km} km',
    excessKmCharge: 'Excess-km charge:',
    waiveExcessKm: 'Waive the excess-km charge',
    notes: 'Notes',
    notesHint: 'Anything else to note. Damage is marked on the inspection, with photos.',
    charges: 'Other charges',
    noCharges: 'No other charges.',
    addCharge: 'Add a charge',
    removeCharge: 'Remove charge {number}',
    chargeNumber: 'Charge {number}',
    chargesFull: 'At most {max} charges per return: remove one to add another.',
    chargeKind: 'Kind',
    chargeDescription: 'Description',
    chargeAmount: 'Amount (€)',
    submit: 'Record the return',
    /** UI-1d: the vehicle's section, and the rail with the button and what it waits for. */
    readings: 'The vehicle as it comes back',
    railTitle: 'To take back',
    railDetails: 'The account',
    moneyHint:
      'The return’s charges join the account when you record it; settle it from the booking.',
    notOut: 'This booking has no vehicle out to take back.',
    alreadyBack: 'The vehicle came back on {date}.',
    /** What the form still lacks, beside the button. */
    why: {
      km: 'Enter the kilometres.',
      kmBelow: 'Check the kilometres: fewer than at hand-over.',
      fuel: 'Choose the fuel level.',
      reading: 'One moment: reading the photos kept on this iPad.',
      unsent: 'Inspection photos not yet sent from this iPad.',
      unsentReason: 'Enter why it goes ahead without the photos, at least 3 characters.',
      charges: 'Complete each charge: a description and an amount.',
    },
  },

  chargeKinds: {
    fuel: 'Refuelling',
    late: 'Late return',
    cleaning: 'Cleaning',
    damage: 'Damage',
    other: 'Other',
    fine_fee: 'Fine handling fee',
    excess_km: 'Excess kilometres',
  },

  money: {
    title: 'Account',
    total: 'Booking',
    paid: 'Paid',
    balance: 'Still to pay',
    settled: 'Settled',
    credit: 'To refund',
    fee: 'Cancellation fee kept',
    feeRules: {
      policy: 'from the terms {version}',
      override: 'reduced: {note}',
      typed: 'typed at the counter',
      legacy: 'the whole total: cancelled before fees existed',
    },
    endedTotal: 'The booking was worth {amount}: only the fee is owed now.',
    refunded: 'Refunded',
    lostInDisputes: 'Given back by the bank (dispute)',
    creditHint: 'More than was owed has been paid: the difference goes back to the customer.',
    onlineBy: 'Stripe',
    payments: 'Payments',
    noPayments: 'No payment recorded.',
    paymentLine: '{amount}, {method}, {date}, {name}',
  },

  disputes: {
    title: 'Disputes by the bank',
    status: {
      warning_needs_response: 'Bank inquiry: needs an answer',
      warning_under_review: 'Bank inquiry: under review',
      warning_closed: 'Bank inquiry: closed',
      needs_response: 'Dispute: needs an answer',
      under_review: 'Dispute: under review',
      won: 'Dispute won',
      lost: 'Dispute lost',
      prevented: 'Dispute prevented',
    },
    /** The Owner's CSV and e-mail keep Stripe's own status, as the Owner's records (UI-2e). */
    unknownStatus: 'Stripe status: {status}',
    statusUnknown: 'A new status: read it in Stripe’s Dashboard',
    otherReason: 'another reason',
    reasons: {
      fraudulent: 'the customer says they did not make the payment',
      duplicate: 'paid twice',
      product_not_received: 'service not received',
      product_unacceptable: 'service not as described',
      credit_not_processed: 'refund not received',
      unrecognized: 'payment not recognised',
      subscription_canceled: 'subscription cancelled',
      general: 'general',
    },
    line: '{amount}, {reason}, opened on {date}',
    answerBy: 'Answer in Stripe’s Dashboard by {date}.',
    openHint:
      'While the bank decides, nothing of this payment goes back by any means, and the booking cannot be closed.',
    returnedHint:
      'The bank gave the money back to the customer: what is owed again is taken at the counter only, never by link.',
  },

  payment: {
    title: 'Record a payment',
    method: 'Method',
    amount: 'Amount (€)',
    amountInvalid: 'Enter an amount, for example 70 or 70.50.',
    reference: 'Reference',
    referenceHint: 'The card terminal slip or the transfer reference.',
    submit: 'Record the payment',
    /** UI-1c: what still stops the payment, beside its button. */
    why: {
      method: 'Choose how the customer pays.',
      amount: 'Enter the amount.',
      overBalance: 'At most what is left to pay: {amount}.',
      drawer: 'Cash needs an open drawer.',
    },
    done: 'Payment recorded.',
    paidOnline:
      'The customer has just paid online: the payment is in the list above. Do not take the money again.',
    pageOpen:
      'Stripe did not answer in time: the customer’s online payment page stays open until {time}. If they pay there too, the account will show what to refund.',
  },

  refund: {
    title: 'Give money back',
    intro: '{amount} is owed back to the customer. Money goes back the way it came.',
    payment: 'From which payment',
    paymentOption: '{amount}, {method}, {date}: {left} can go back',
    method: 'How',
    methods: {
      cash: 'In cash',
      card_terminal: 'To the card, with the shop’s card terminal',
      bank_transfer: 'By bank transfer',
      stripe: 'Through Stripe, to the card used online',
    },
    amount: 'Amount (€)',
    amountHint: 'At most {amount}.',
    amountInvalid: 'Enter an amount up to {amount}, for example 20 or 20.50.',
    reason: 'Reason',
    reasonHint: 'Required: it stays in the change log.',
    reasonShort: 'Enter at least 3 characters.',
    reference: 'Reference',
    referenceHint: 'The card terminal slip or the transfer reference.',
    submit: 'Record the refund',
    submitStripe: 'Refund through Stripe',
    confirm: {
      title: 'Give {amount} back to the customer?',
      final: 'This cannot be undone.',
      confirm: 'Give back {amount}',
    },
    why: {
      payment: 'Choose which payment.',
      method: 'Choose how to give it back.',
      amount: 'Enter the amount.',
      overMax: 'At most {amount}.',
      reason: 'Enter the reason, at least 3 characters.',
      drawer: 'Giving back in cash needs an open drawer.',
    },
    done: 'Refund recorded.',
    sent: 'Refund asked of Stripe: it leaves in a few seconds.',
    noPayment: 'None of this booking’s payments can be given back from here.',
    ownerOnly: 'The Owner gives it back: the booking is on Today’s “To give back” list.',
    list: 'Refunds',
    line: '{amount}, {method}, {date}',
    by: 'by {name}',
    byStripe: 'in Stripe’s Dashboard',
    states: {
      pending: 'On its way with Stripe',
      requires_action: 'Stripe needs an action: open it in Stripe’s Dashboard',
      succeeded: 'Given back',
      failed: 'Failed: give it back another way',
      canceled: 'Cancelled',
    },
    superseded: 'Not sent: it had already been given back another way.',
    notSent: 'Not sent: Stripe never received it. Ask for it again.',
    sync: 'Check with Stripe',
    synced: 'Checked with Stripe.',
  },

  /**
   * S6 (CMP-2): the letter naming a fine's renter to the authority. Rendered
   * in Italian for every authority; this copy is for reading only.
   */
  fineLetter: {
    title: 'Driver details',
    to: 'To: {authority}',
    subject:
      'Re: notice no. {number}, served on {notified}. The driver’s details (articles 126-bis and 196 of the Highway Code).',
    body: 'Regarding the notice above, for the offence recorded on {date} at {time} in {place}, with the vehicle registered {plate} ({model}), the company below states that at that moment the vehicle was rented without a driver to the person below, from {from} to {to} (booking {code}{contract}).',
    stillOut: 'rental still running',
    contractSigned: ', contract signed on {date}',
    contractPaper: ', contract no. {reference}',
    sections: { company: 'Rental company', renter: 'Driver (renter)' },
    fields: {
      name: 'Company name',
      address: 'Registered office',
      vatNumber: 'VAT number',
      surname: 'Surname',
      givenNames: 'Given names',
      birthDate: 'Date of birth',
      birthPlace: 'Place of birth',
      residence: 'Residence',
      licence: 'Licence no.',
      licencePlace: 'Issued by',
      licenceExpires: 'Expires',
      licenceCategories: 'Categories',
    },
    missing: 'to be completed',
    attachment: 'A copy of the rental contract is attached.',
    place: 'Place and date',
    signature: 'Stamp and signature',
    footer: 'Made on {date} · notice {number} · booking {code}',
  },
  /** S6 (CMP-2, decision 4): the fines register, for the holders of fines.manage. */
  fines: {
    title: 'Fines',
    intro:
      'The notices the company received: each matched to the rental that had the car, the driver’s details sent to the authority before the deadline, the handling fee charged.',
    views: { open: 'To handle', all: 'All' },
    search: 'Search',
    searchHint: 'Plate or notice number.',
    register: 'Register a notice',
    none: 'No fines.',
    found: { one: '{count} fine', other: '{count} fines' },
    fills: 'A fine arrives here with “Register a notice”.',
    /** UI-2g: «To handle» empty is not the register empty. */
    noneOpen: 'No fines to handle.',
    fillsOpen: 'Those already closed are in “All”.',
    shown: '{shown} of {total}: search to find the others.',
    columns: {
      notice: 'Notice',
      violated: 'Offence',
      plate: 'Plate',
      booking: 'Booking',
      status: 'Status',
      deadline: 'Deadline',
    },
    status: {
      registered: 'Registered',
      matched: 'Matched',
      communicated: 'Sent',
      closed: 'Closed',
      not_ours: 'Not ours',
      withdrawn: 'Withdrawn',
    },
    deadline: {
      running: 'Running',
      due_soon: 'Due soon',
      overdue: 'Overdue',
      met: 'Met',
      late: 'Sent late',
      none: 'None',
    },
    daysLeft: { one: '{count} day left', other: '{count} days left' },
    dueToday: 'due today',
    daysPast: {
      one: 'overdue by {count} day',
      other: 'overdue by {count} days',
    },
    feeCharged: 'Handling fee charged',
    channels: {
      pec: 'PEC',
      form: 'Authority’s form',
      portal: 'Online portal',
      post: 'Registered letter',
      other: 'Other',
    },
    form: {
      titleNew: 'Register a notice',
      titleCorrect: 'Correct the notice',
      hint: 'As printed on the notice. The deadline to send the driver’s details counts from the day it was served.',
      authority: 'Authority',
      authorityHint: 'Who issued the notice: the police or the comune.',
      noticeNumber: 'Notice number',
      violatedOn: 'Date of the offence',
      violatedAt: 'Time of the offence',
      place: 'Place',
      plate: 'Plate',
      amount: 'Amount of the fine',
      amountHint: 'The driver pays it to the authority: it is not the company’s money.',
      notifiedOn: 'Served on',
      submit: 'Register',
      save: 'Save the correction',
      cancel: 'Cancel',
      invalid: 'Check the fields marked.',
      errors: {
        authority: 'Enter the authority that issued the notice.',
        noticeNumber: 'Enter the notice number.',
        violatedOn: 'Choose the day of the violation.',
        violatedAt: 'Enter the time as on the notice, for example 14:30.',
        place: 'Enter where the violation happened.',
        plate: 'A plate is letters and digits.',
        amount: 'Enter the amount in euros, for example 42.00.',
        notifiedOn: 'Choose the day the notice arrived.',
      },
    },
    detail: {
      back: 'All fines',
      heading: 'Fine',
      title: 'Notice {number}',
      notFound: 'No fine at this address.',
      notice: 'The notice',
      when: '{date} at {time}',
      authority: 'Authority',
      violated: 'Offence',
      place: 'Place',
      plate: 'Plate',
      vehicle: 'Vehicle',
      notOurPlate: 'None of our vehicles has this plate.',
      amount: 'Amount',
      notified: 'Served on',
      deadline: 'Deadline',
      registered: 'Registered by {name} on {when}.',
      correct: 'Correct the notice',
    },
    rental: {
      title: 'Rental',
      noVehicle: 'The plate is not one of our vehicles’: if so, mark the fine as not ours.',
      none: 'No rental had the car at that minute: a mistake in the notice or the plate, or the car was at the office.',
      several: 'Several rentals had the car at that minute: pick the right one.',
      pick: 'Match to rental {code}',
      noneNamed: 'No rental matched.',
      period: 'From {from} to {to}',
      stillOut: 'still out',
      auto: 'Found by itself: the only rental that had the car at that minute.',
      manual: 'Picked by {name} on {when}.',
      unmatch: 'Undo the match',
      unmatchHint: 'Only while nothing was sent and no fee is charged.',
    },
    letter: {
      title: 'Letter with the driver’s details',
      intro:
        'In Italian, for the authority: the driver’s details recorded at hand-over. It never shows the ID document’s number.',
      prepare: 'Prepare the letter',
      preparing: 'Being prepared…',
      failed:
        'The letter was not prepared: try again; if it happens again, look at the Sistema screen.',
      retry: 'Try again',
      open: 'Open the letter (PDF)',
      notInApp:
        'The letter does not open in this app right now: close and reopen it, or open the letter from a computer.',
      ready: 'Letter ready since {when}.',
      // S8: a renter anonymised, or a rental naming someone anonymised.
      renterAnonymised:
        'This rental’s customer was anonymised: no letter can name them any more. Handle the fine by hand.',
      ownerOnly: 'This rental names an anonymised person: only the Owner opens its letter.',
      viewLogged: 'This rental names an anonymised person: every view of its letter is logged.',
    },
    communication: {
      title: 'Sent to the authority',
      intro: 'Once the driver’s details were sent to the authority, record the day and the way.',
      on: 'Sent on',
      channel: 'How',
      reference: 'Reference',
      referenceHint: 'The PEC’s, the form’s or the registered letter’s number.',
      submit: 'Record the sending',
      done: 'Sent on {date} by {channel}; recorded by {name}.',
      doneReference: 'Sent on {date} by {channel} ({reference}); recorded by {name}.',
      why: 'Choose the day they were sent.',
    },
    fee: {
      title: 'Handling fee',
      none: 'The rental left with no fines handling fee: there is nothing to charge.',
      notYet: 'It is charged once the fine is matched to the rental.',
      charge: 'Charge {amount}',
      confirm:
        'Charge {amount} to booking {code}? It is collected at the counter or by a payment link, never from the card by itself.',
      confirmYes: 'Yes, charge it',
      charged: 'Charged {amount} on {when}.',
      balance:
        'Booking {code} still owes {amount}: collect it from its page, at the counter or by a payment link.',
      paid: 'Booking {code} owes nothing more.',
    },
    end: {
      title: 'Ending the fine',
      notOurs: 'Not ours',
      withdrawn: 'Withdrawn by the authority',
      close: 'Close the fine',
      reopen: 'Reopen',
      reason: 'Reason',
      submit: 'Confirm',
      cancel: 'Cancel',
      ended: '{status} on {when} by {name}.',
      endedReason: '{status} on {when} by {name}: {reason}',
      why: 'Enter the reason, at least {min} characters.',
    },
    done: {
      registered: 'Notice registered.',
      corrected: 'Notice corrected.',
      matched: 'Fine matched to the rental.',
      unmatched: 'Match undone.',
      letter: 'The letter is being prepared.',
      communicated: 'Sending recorded.',
      fee: 'Handling fee charged.',
      ended: 'Done.',
      reopened: 'Fine reopened.',
    },
    mentions: {
      title: 'Fines',
      line: '{authority}, notice {number}: {date} at {time}, {plate}',
      fee: 'fee {amount}',
    },
  },
  fiscal: {
    title: 'Fiscal',
    intro:
      'Each point’s till prints the fiscal documents: their numbers are typed here. Below, the payments and refunds that have none yet.',
    providerPending: 'Fiscal document provider: every document is typed from the printed one.',
    money: { payment: 'Payment', refund: 'Refund' },
    methods: {
      cash: 'Cash',
      card_terminal: 'Card terminal',
      bank_transfer: 'Bank transfer',
      online: 'Online, with Stripe',
      deposit: 'From the deposit',
      old_site: 'On the old site',
      stripe: 'With Stripe',
    },
    kinds: {
      receipt: 'Receipt',
      return: 'Return',
      invoice: 'Invoice',
      credit_note: 'Credit note',
    },
    sources: {
      till: 'The point’s till',
      provider: 'The provider',
      other: 'Other',
    },
    document: {
      legend: 'Fiscal document',
      hint: 'The printed number. Without one the money stays on the Fiscal screen, to complete.',
      kind: 'Kind',
      source: 'Printed by',
      point: 'Till’s point',
      pointFromDrawer: 'The till at the cash drawer’s point.',
      pointChoose: 'Choose the till’s point.',
      pointPrompt: 'Choose the point',
      number: 'Number',
      numberHint: 'As printed, for example 0042-0007.',
      printedLater: 'Printed at another time',
      printedDate: 'Printed on',
      printedTime: 'At',
      printedHint: 'The date and time decide which closure (Z) it belongs to.',
      printedInvalid: 'Enter a valid date and time.',
      stripeLater:
        'With Stripe the money goes back later: the document is typed from the Fiscal screen once the refund has succeeded.',
    },
    line: '{kind} no. {number}, {date}',
    linePoint: '{kind} no. {number}, {date}, {point}',
    none: 'No fiscal document',
    // S9 (Q-120): what was paid on the old site, and what goes back from it, has none here.
    notHere: 'No fiscal document here: money of the old site.',
    record: 'Record the document',
    recordTitle: 'Fiscal document for {amount}',
    recordSubmit: 'Record',
    recordFor: 'Record the document: {code}, {amount}',
    recorded: 'Document recorded.',
    cancel: 'Cancel',
    correct: 'Correct',
    correctTitle: 'Correct document no. {number}',
    correctReason: 'Reason',
    correctReasonHint: 'Required: the wrong document is kept, with the reason.',
    correctWithRight: 'Enter the right document too',
    correctSubmit: 'Correct the document',
    /** UI-1e: what still stops a document's button, beside it. */
    why: {
      number: 'Enter the document’s number.',
      reason: 'Enter the reason, at least 3 characters.',
    },
    corrected: 'Document corrected.',
    superseded: '{kind} no. {number}, corrected on {date}: {reason}',
    missing: {
      title: 'Without a fiscal document',
      when: 'When',
      booking: 'Booking',
      money: 'Money',
      method: 'Method',
      amount: 'Amount',
      totals: {
        one: '{count} item, {amount}.',
        other: '{count} items, {amount}.',
      },
      shown: '{shown} of {count} are listed: narrow the period.',
      none: 'Every payment and refund in this period has its document.',
    },
    till: {
      title: 'Till closure (Z)',
      closeHint:
        'If this point’s till has made its daily closure, enter the Z: it is set against the recorded documents. Without a number the drawer closes without a Z.',
      aloneTitle: 'Record a Z',
      aloneHint: 'A till closure made without closing the drawer, or forgotten.',
      number: 'Z number',
      date: 'Printed on',
      time: 'At',
      total: 'Total (€)',
      totalHint: 'The printed total, already net of returns.',
      incomplete: 'A Z needs its number, date and time, and total.',
      difference:
        'The till documents recorded for this Z add up to {documents}: difference {difference}.',
      reason: 'Reason for the Z’s difference',
      reasonHint: 'Required: the Owner reads it.',
      whyReason: 'Enter the reason for the difference, at least 3 characters.',
      submit: 'Record the Z',
      recorded: 'Z recorded.',
      list: 'Latest Zs',
      listOf: 'Latest Zs, {point}',
      listFailed: 'The latest Zs did not load.',
      none: 'No Z recorded for this point.',
      columns: {
        number: 'Z no.',
        closedAt: 'Printed',
        total: 'Total',
        documents: 'Documents',
        difference: 'Difference',
        now: 'Now',
        reason: 'Reason',
      },
      liveHint: '“Now”: the difference with the documents recorded today, after the Z too.',
      superseded: 'Corrected on {date}: {reason}',
      correct: 'Correct',
      correctTitle: 'Correct Z no. {number}',
      correctWithRight: 'Enter the right Z',
      correctSubmit: 'Correct the Z',
      corrected: 'Z corrected.',
    },
  },

  paymentMethods: {
    cash: 'Cash',
    card_terminal: 'Card, on the shop’s terminal',
    bank_transfer: 'Bank transfer',
    online: 'Online, with Stripe',
    deposit: 'Kept from the deposit',
    old_site: 'From the old site',
  },

  sources: {
    walk_in: 'Walk-in',
    phone: 'Phone',
    whatsapp: 'WhatsApp',
    email: 'E-mail',
    web: 'Website',
    old_site: 'From the old site',
  },

  eligibility: {
    eligible: 'Requirements met',
    ineligible: 'Requirements not met',
    unknown: 'Check at the counter',
  },

  eligibilityReasons: {
    rules_not_configured: 'Driver requirements are not configured for this category.',
    birth_date_missing: 'Date of birth not given.',
    too_young: 'Minimum age: {minAge}.',
    licence_rules_not_configured: 'The required licence is not configured for this category.',
    licence_missing: 'Licence not given.',
    licence_expired: 'Licence expired: {expiresOn}.',
    licence_category: 'Licence category: {accepted} accepted.',
    licence_first_issue_unknown: 'First issue date of the licence not given.',
    licence_too_recent: 'Licence held for less than {minYears} years.',
    idp_required: 'An International Driving Permit is required (licence: {country}).',
    manual_check: 'Licence to check, as for every rental in this category.',
  },

  documentKinds: {
    id_card: 'Identity card',
    passport: 'Passport',
    driving_licence: 'Driving licence',
    other: 'Other document',
  },

  documentSides: {
    front: 'front',
    back: 'back',
    single: 'single page',
  },

  mediaStatus: {
    pending: 'uploading',
    stored: 'received',
  },

  vehicleDocuments: {
    insurance: 'Insurance',
    revisione: 'Inspection (revisione)',
    bollo: 'Road tax (bollo)',
    other: 'Other document',
  },

  quoteLines: {
    rental: 'Rental',
    surcharge: 'Surcharge',
    discount: 'Discount {code}',
    delivery: 'Delivery: {name}',
    collection: 'Collection at the end: {name}',
    one_way: 'Return to another place',
    extra: '{name}',
    coverage: '{name}',
  },
  youngDriver: 'Young driver surcharge',

  errors: {
    network: 'The server is not answering. Check the connection and try again.',
    fallback: 'Something went wrong. Please try again shortly.',
    notConfigured: {
      'terms.current_version': 'No terms are in force: the Owner must load them.',
      'company.legal':
        'The company’s details for the contract are missing (legal name, address, VAT number): the Owner must complete them.',
      'catalog.demo': 'This system holds the sample data: production never sells it.',
      other: 'The configuration is incomplete: the Owner must complete it.',
    },
    /** The counter's own words for codes the public endpoints share (C1). */
    shared: {
      booking_closed: 'This booking is closed: no more documents can be added.',
      booking_disputed:
        'The customer disputed a payment with their bank: what they owe is taken at the counter only, with no link.',
      terms_draft:
        'The terms in force are a draft: no contract can be signed. The Owner must load the final terms.',
    },
    client: {
      device_store_unavailable:
        'The credentials saved on this device cannot be read right now. Try again shortly; if it persists, close the app and open it again.',
      file_type_refused: 'This file is neither a photo nor a PDF: the app does not show it.',
      file_not_opened: 'The file did not open: try again.',
      // S2: anything that is not an answer from the API (a bug in the app).
      unexpected: 'Something went wrong on this screen. Try again; if it goes on, reload the app.',
    },
    codes: {
      credentials_invalid: 'The e-mail or the password is not right.',
      totp_required: 'Enter the code from the authenticator app.',
      totp_invalid: 'The code is wrong or already used: wait for the next one and try again.',
      totp_not_configured: 'This account has no authenticator app set up: ask the owner.',
      device_not_enrolled: 'This device is not enrolled.',
      device_revoked: 'This device has been signed out.',
      session_required: 'Screen locked: enter your PIN.',
      user_not_found: 'This person can no longer sign in.',
      pin_not_set: 'No PIN yet: sign in with your password to choose one.',
      pin_invalid: 'Wrong PIN.',
      pin_locked: 'Too many wrong PINs: this device now needs your password.',
      reauthentication_required: 'Changing the PIN needs a recent password sign-in.',
      pin_format: 'The PIN has 6 digits.',
      pin_too_simple: 'That PIN is too easy: avoid sequences and repeats.',
      // OPS-1, Regole del contratto.
      terms_already_current: 'This version is already in force.',
      terms_already_stored: 'This text is already stored as version {version}.',
      forbidden: 'Your role does not allow this.',
      contact_required:
        'A phone, WhatsApp or e-mail booking needs the customer’s phone number or e-mail.',
      customer_not_found: 'Customer not found: search again.',
      checkout_too_early:
        'Too early: the vehicle can leave from two hours before the booked pick-up.',
      checkout_too_late: 'The booked return time has passed: this booking can no longer leave.',
      checkout_blocked: 'The hand-over is blocked by what is listed here.',
      vehicle_required: 'Choose the vehicle that leaves.',
      not_reschedulable: 'This booking cannot be moved: it is already out or closed.',
      km_below_checkout: 'The km reading is below the one at hand-over: check it.',
      payment_exceeds_balance: 'The amount is more than what is still owed.',
      paid_online_meanwhile:
        'The customer has just paid online: the payment is recorded. Check the account before taking any money.',
      customer_email_missing:
        'The booking has no e-mail address: make the link and copy it into the message.',
      contract_already_signed:
        'This booking’s contract is already signed for this vehicle: there is no need to sign it again.',
      deposit_already_taken: 'This booking already has a deposit.',
      deposit_not_needed: 'This category takes no deposit.',
      deposit_not_pending: 'The reader is not waiting for a card.',
      deposit_not_held: 'The card has not been held yet.',
      deposit_exceeds_hold: 'The amount is more than the deposit.',
      deposit_exceeds_balance:
        'The amount is more than what is still owed: only what is owed is kept from the deposit.',
      deposit_expired: 'The card hold lapsed before it could be kept: nothing was taken.',
      no_deposit: 'This booking has no deposit.',
      cash_not_accepted: 'This category does not take a cash deposit.',
      reader_pending:
        'The card reader is not live yet: record the deposit taken on the POS or in cash.',
      reader_unavailable:
        'The card reader does not answer or is busy: try again, or use the shop’s POS.',
      simulation_refused: 'Test cards work only in test mode.',
      inspection_closed:
        'The vehicle has already left or is already back: this inspection can no longer change.',
      not_this_vehicle: 'This booking has another vehicle.',
      zone_not_on_vehicle: 'This vehicle has no such zone.',
      damage_conflict: 'This damage was already recorded with other details: reload the page.',
      damage_not_on_vehicle: 'The damage is not open on this vehicle, in this zone.',
      intervention_voided: 'This intervention has been voided.',
      photo_conflict:
        'This photo was already sent with other details: discard it and take it again.',
      photo_other_device:
        'This photo was started on another device, or before this one was enrolled again: discard it and take it again.',
      photo_erased: 'This photo was erased with its booking, which ended long ago: discard it.',
      damage_not_chargeable: 'Only a damage marked at this return can be charged, once, as damage.',
      too_many_photos: 'This inspection already has the most photos allowed.',
      too_many_damages: 'This inspection already has the most damages allowed.',
      vehicle_still_out:
        'The vehicle is not back yet from booking {bookingCode}: record that return first, or choose another vehicle.',
      origin_not_allowed: 'The server is not set up for this app. Tell the Owner.',
      credentials_ambiguous:
        'This device sent its credentials two ways. Close the app and open it again.',
      auth_mode_required: 'This version of the app cannot sign in: update it.',
      credentials_malformed: 'This device’s credentials cannot be read. Enroll the device again.',
      cross_site_refused: 'Open photos and documents from the counter app.',
      not_dead: 'This message is no longer among those not sent: someone may have queued it again.',
      not_resendable: 'Only e-mails can be sent again from here.',
      identity_required: 'Record the driver’s details first.',
      identity_changed: 'The driver’s details were recorded again meanwhile: check and try again.',
      cargos_details_required:
        'This vehicle also needs the document, the licence, the place of birth and citizenship.',
      cargos_not_required:
        'This category is not reported to CaRGOS: the document number is not recorded.',
      cargos_record_invalid: 'CaRGOS would refuse these details: correct the fields shown.',
      cargos_already_reported: 'CaRGOS already has the receipt for this hand-over.',
      cargos_in_flight: 'A report to CaRGOS is already on its way: wait for the answer.',
      cargos_outcome_unknown:
        'The last report’s outcome is unknown: first check on the CaRGOS portal that the report is not there.',
      cargos_pending: 'Sending to CaRGOS is not active yet: use the portal and record the receipt.',
      contract_not_signed: 'Have the contract for this vehicle signed first.',
      cancellation_fee_required:
        'This booking’s terms say nothing about cancelling: enter what is kept.',
      cancellation_fee_not_allowed:
        'That fee is not allowed: it never exceeds the booking’s total, and a reduction is lower than the fee the terms set.',
      cancellation_fee_changed:
        'The fee changed while the window was open: check the new amount and confirm again.',
      no_show_too_early: 'A no-show is marked only after the booked pick-up time.',
      draft_paid_meanwhile:
        'The customer has just paid their share online: the booking is confirmed. Reload the page; if it must be cancelled, it is cancelled with the fee in its terms.',
      deposit_still_held:
        'A deposit is still held: release or keep it first, in the Deposit panel.',
      account_not_settled:
        'The account is not settled: first take what is owed or give back what is due.',
      refund_in_progress: 'A refund is still on its way: the booking closes once it is done.',
      dispute_open:
        'The customer’s bank is still deciding a dispute: the booking closes once it ends.',
      payment_page_open:
        'Stripe did not answer: the customer’s online payment page may still take money. Try again in a few minutes.',
      cash_drawer_required:
        'Cash moves only through an open drawer: choose the drawer, or open it with a count.',
      cash_drawer_closed:
        'This drawer is closed, or being closed: open it again with a count, or choose another.',
      cash_drawer_open: 'This pick-up point’s drawer is already open.',
      cash_difference:
        'The count is not what the drawer should hold: give the reason, or count again.',
      fiscal_kind:
        'Wrong kind of document: a receipt or an invoice for money taken, a return or a credit note for money given back; the till issues receipts and returns only.',
      fiscal_location: 'Say which point’s till issued the document.',
      fiscal_document_exists:
        'This money already has its fiscal document: correct it if it is wrong.',
      fiscal_document_not_yet:
        'The refund has not gone through yet: its document is recorded once the money is back.',
      fiscal_not_here:
        'What was paid on the old site, and what goes back from it, has no fiscal document here.',
      old_site_reference_taken:
        'This booking from the old site has been recorded already: find it by its reference.',
      stripe_payment_taken: 'This Stripe payment is recorded on a booking already.',
      old_site_payment_unchecked:
        'Stripe is not configured, so the payment cannot be checked. Record it without its Stripe id (it will go back by bank transfer only), or try again once Stripe is set up.',
      old_site_payment_unknown:
        'Stripe has no payment with this id: check that it is the right one (it starts with pi_).',
      old_site_payment_ours:
        'This payment was taken by this app (a payment page or a deposit), not by the old site.',
      old_site_payment_not_paid: 'Stripe says this payment was not taken.',
      old_site_payment_currency: 'This payment is not in euros.',
      old_site_payment_amount:
        'For this payment Stripe holds {stripeNet}, after refunds: correct the amount paid, or the id.',
      old_site_payment_disputed:
        'A bank has disputed this payment: check in Stripe what the customer really paid, then record it without its Stripe id.',
      old_site_payment_refunds:
        'Stripe holds a refund of this payment that cannot be recorded here: record it without its Stripe id, with what the customer really paid.',
      fiscal_superseded: 'This document was already corrected.',
      vehicle_document_superseded: 'This document was already cancelled.',
      till_closure_exists: 'This closure is already recorded (same number or same time).',
      fine_exists: 'This notice from this authority is registered already.',
      fine_status: 'The fine is no longer at this step: reload the page.',
      fine_plate: 'A plate is letters and digits.',
      fine_dates:
        'Check the dates: a notice arrives after the offence and no later than today; the data is sent after it arrived and no later than today.',
      fine_not_candidate: 'That rental did not have the car at the offence’s minute.',
      fine_not_matched: 'The fine is not matched to a rental yet.',
      fine_reason_required: 'Give the reason.',
      fine_fee_charged: 'This fine’s handling fee is charged already.',
      fine_fee_not_configured:
        'The rental left with no fines handling fee: there is nothing to charge.',
      fine_renter_anonymised:
        'The renter’s data was erased for privacy: no letter can name them. Handle the fine by hand.',
      customer_anonymised:
        'This customer was anonymised for privacy: their record is never changed again.',
      contact_erased:
        'This customer’s contact details were erased at their request: they cannot be written back.',
      contract_being_signed:
        'The customer is signing a contract: correct the details once they have signed.',
      second_driver_unavailable:
        'A second driver cannot be added to this booking now: reload the page.',
      second_driver_ineligible:
        'The second driver does not meet this category’s requirements: only the Owner can let them drive, with a reason.',
      second_driver_is_renter:
        'Whoever booked cannot also be the second driver: choose another person.',
      no_second_driver: 'This booking has no second driver: reload the page.',
      second_identity_required: 'First record the second driver’s details.',
      unknown_category: 'This category no longer exists: reload the page.',
      unknown_site_model: 'The website has no such model: reload the page.',
      model_exists: 'This model is already in the list: find it there.',
      site_model_fields:
        'A website model keeps the website’s name and details: change them on the website.',
      name_required: 'Give the model a name.',
      photo_required: 'A model added here needs a photo.',
      photo_not_ready: 'The photo has not finished uploading: wait a moment, or take it again.',
      lead_needs_category: 'Only a model linked to a category can stand for it.',
      lead_changed: 'Someone else changed which model stands for this category: reload the page.',
      retired_linked: 'Unlink the model from its category before taking it out of use.',
      customer_erasure_requested:
        'This customer asked for their data to be erased: their record takes no new bookings. Book them as a new customer.',
      erasure_already_requested: 'A request to erase this customer’s data is already on record.',
      no_erasure_request: 'No request to erase is on record: reload the page.',
      erasure_grace_over:
        'The 24 hours to withdraw the request are over: the erasure has gone ahead, or goes ahead at the next run.',
      legal_hold_exists: 'A legal hold already stands on this customer.',
      no_legal_hold: 'No legal hold stands on this customer: reload the page.',
      export_ticket_invalid:
        'The download has expired, or was asked for in another session: ask for the export again with the code.',
      fines_not_configured:
        'The days to send the driver’s details are not set (fines.communication_days).',
      till_difference:
        'The closure’s total does not match the documents recorded: give the reason.',
      report_range:
        'The period runs from its first day to its last, in that order, and lasts a year and a day at most.',
      difference_acknowledged: 'This difference was already marked as read.',
      nothing_to_refund: 'Nothing is owed back on this booking.',
      refund_exceeds_credit: 'The amount is more than is owed back to the customer.',
      refund_exceeds_payment: 'The amount is more than is left of this payment.',
      refund_method_not_allowed:
        'Money goes back the way it came: choose one of the ways offered for this payment.',
      refund_needs_stripe:
        'Stripe is not set up on this system: a refund through Stripe cannot be made. Give it back another way offered.',
      // S9 (Persone, Q-51)
      password_change_required:
        'Choose your own password before you go on: the one you were given is for the first sign-in only.',
      password_expired: 'This password has expired: ask the Owner for a new one.',
      password_is_email: 'The password cannot be your e-mail.',
      password_unchanged: 'Choose a password other than the current one.',
      email_taken:
        'An account with this e-mail exists already. If the password just made was not shown, give them a new one from their page.',
      role_not_found: 'This role does not exist: reload the page and choose another.',
      last_owner: 'At least one active Owner with an authenticator must remain: add another first.',
      person_self:
        'Not on your own account: another Owner changes your role and access; your password you change under “Change password”.',
      person_disabled: 'This person is deactivated: reactivate them first.',
      person_active: 'This person is active already.',
      no_authenticator: 'This role signs in without a code: there is no authenticator to reset.',
      device_current: 'This is the device you are using: sign it out from Devices.',
      device_not_theirs: 'This person never used that device: reload the page.',
      session_opened: 'This person unlocked a device at that very moment: try again.',
      delivery_not_a_zone: 'This end of the booking is at a pick-up point, not a delivery zone.',
      delivery_closed: 'This trip is done already, or the booking has ended.',
      delivery_person_unavailable:
        'This person is deactivated, or their role does not hand cars over: choose someone else.',
      delivery_given_now: 'This person was given a delivery at that very moment: try again.',
      delivery_zone:
        'The delivery zone does not take this move: deliveries suspended, too little notice, or that day closed.',
      opening_hours:
        'The pick-up point is closed at that time: choose a time within its opening hours.',
      delivery_not_yours: 'This trip is not given to you, or its time is over: ask the office.',
      delivery_office_only: 'On a delivery, the office does this: ask the office.',
      changed_since_preview:
        'Someone changed one of these days after the preview: preview the change again.',
      day_past: 'A day already past is not changed.',
      invalid_range: 'The last day comes before the first.',
      range_too_long: 'A change covers 400 days at most.',
      invalid_weekdays: 'Choose at least one day of the week.',
      price_not_positive: 'A day’s price stays above zero.',
      price_too_high: 'A day’s price is €5,000 at most.',
      // S11: typing paper back in.
      reentry_not_configured:
        'Typing paper back in is not switched on: the settings reentry.max_days and reentry.revenue_day are missing.',
      reentry_empty: 'Nothing to type in: fill in at least one part of the sheet.',
      reentry_time:
        'A time is not right: it cannot be in the future, or further back than the settings allow.',
      reentry_order:
        'The times do not add up: nothing before the booking existed, then receipt, hand-over and return in that order, and the vehicle never with two customers at once.',
      paper_sheet: 'The sheet number is not right: check it again on the printed sheet.',
    },
  },

  /** S2: the error screen, inside the app, when a screen stops working. */
  crash: {
    title: 'This screen stopped working',
    body: 'Try again. If it happens again, reload the app: data already saved is not lost.',
    retry: 'Try again',
    reload: 'Reload the app',
    reference: 'Reference: {id}',
  },

  // Q-157: the models that stand for each category, set by the Owner and the Agents.
  models: {
    title: 'Categories and models',
    intro:
      'The models that stand for each category, on the website and here. The lead gives the photo and the name ("Panda or similar"); the others are listed as possible models.',
    lead: 'Lead',
    appModel: 'Added here',
    ownPhoto: 'Photo taken here',
    noModels: 'No model linked',
    noModelsHint: 'On the website this category shows with no photo, under its own name.',
    noLead: 'No lead model: the website uses the first in the list.',
    inactiveCategory: 'Category not active',
    link: 'Link a model',
    linkTitle: 'Link a model to {category}',
    linkHint: 'The website’s models not yet linked to a category.',
    linkNone: 'Every model on the website is already linked.',
    linked: '{model} linked to {category}.',
    add: 'Add a new model',
    addTitle: 'A model the website does not have',
    addHint:
      'It shows with its name and photo, with no page on the website. A website model’s name and details are changed on the website itself.',
    added: '{model} added.',
    edit: 'Edit',
    editTitle: 'Edit {model}',
    saved: '{model} saved.',
    makeLead: 'Make it the lead',
    madeLead: '{model} now leads {category}.',
    name: 'Name',
    category: 'Category',
    noCategory: 'No category (unlinked)',
    isLead: 'The category’s lead model',
    leadTaken: 'It takes the place of {model}.',
    active: 'In use',
    retireHint: 'Out of use, it no longer shows on the website or among the models to link.',
    photo: 'Photo',
    newPhoto: 'New photo',
    photoHint:
      'It will appear on the website: no readable plates, people or office interiors. The photo’s data (location, camera) is removed.',
    sitePhoto: 'Go back to the website’s photo',
    seats: 'Seats',
    seatsInvalid: 'From 1 to 9, or leave it empty.',
    transmission: 'Gearbox',
    fuel: 'Fuel',
    notGiven: 'Not given',
    specsHint:
      'A detail left empty does not show: the category shows only what all its models have.',
    unlinked: 'Unlinked models',
    unlinkedHint: 'They stand for no category: the website does not show them in the results.',
    retired: 'Out of use',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    sending: 'Sending the photo…',
    reading: 'Reading the photo…',
    unreadable: 'This photo cannot be read: take or choose another.',
    photoRequired: 'Take or choose a photo.',
    nameRequired: 'Write the model’s name.',
    photoOf: 'Photo of {model}',
    preview: 'Preview of the new photo',
  },

  /** S2: the Sistema screen (permission system.view) and its banner. */
  /** OPS-1: the rental terms' wording, the Owner's (Regole del contratto). */
  terms: {
    title: 'Contract rules',
    intro:
      'The rental terms every new contract carries. A change is a new version: contracts already prepared or signed stay as they are.',
    none: 'No version is in force: no contract can be signed and no online booking made until the Owner puts one in force.',
    editor: 'Text',
    editing: 'Starting from version {version}. Saving makes a new version: none is ever edited.',
    editingNone: 'No version stored: write the first one.',
    language: 'The text is Italian for every customer.',
    versionTitle: 'Title',
    clauses: 'Clauses',
    clause: 'Clause {n}',
    clauseUnnumbered: 'Unnumbered paragraph',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    add: 'Add a clause',
    approval: 'Specific approval (arts. 1341 and 1342 c.c.)',
    approvalHint:
      'The clause numbers cited here are never rewritten by the app: after a move or a removal, check them yourself.',
    renumbered:
      'You moved or removed clauses: the numbers changed. Check the references in the specific approval.',
    cited: 'The numbers the approval cites, and the clause each has today',
    secondDriverAllowed: 'These terms let the second driver drive',
    secondDriverHint:
      'Tick only if the text says the second driver named on the contract may drive too. Without it, only the signer drives.',
    secondDriverHintFrom:
      'Version {version} allowed it: it is not carried over by itself, confirm it for these words.',
    secondDriverYes: 'Second driver allowed.',
    secondDriverNo: 'Only the signer drives.',
    citedRow: 'no. {n}: {text}',
    citedMissing: 'no. {n}: no clause with this number',
    cancellation: 'Cancellation fees',
    cancellationHint:
      'Changed with the configuration, not here: the new version copies them from the version in force.',
    cancellationNone: 'These terms say nothing about cancelling: the counter types the fee.',
    tier: 'From {hours} hours before the pick-up: {fee}',
    noShow: 'No-show: {fee}',
    fee: '{percent} % of the total + {fixed}',
    preview: 'Preview',
    previewHint: 'As the customer reads and signs it.',
    untitled: '(no title)',
    save: 'Save as a new version',
    saved: 'Version {version} saved. Not in force yet: put it in force from the list.',
    unchanged: 'The text is the same as version {version}: nothing to save.',
    empty: 'A title, at least one clause and the specific approval are needed.',
    discard: 'Discard the changes',
    discardTitle: 'Discard the unsaved changes?',
    discardBody: 'What you wrote will be lost.',
    discardConfirm: 'Discard',
    discardKeep: 'Keep writing',
    history: 'Versions',
    noVersions: 'No version stored.',
    versionLabel: 'Version {version}',
    current: 'In force',
    draft: 'DRAFT',
    inEditor: 'In the editor',
    created: 'Created on {date} by {by}',
    fromFiles: 'configuration',
    unknownAuthor: 'app',
    inForceSince: 'In force since {date}',
    open: 'Open in the editor',
    activate: 'Put in force',
    activateTitle: 'Put version {version} in force?',
    activateBody:
      'From now on every new contract carries this text. Contracts already prepared or signed do not change.',
    activateDraft: 'It is a DRAFT: contracts on it have no legal value, and production refuses it.',
    reason: 'Reason',
    reasonHint: 'Goes into the change log, with the version before and after.',
    code: 'Authenticator app code',
    confirm: 'Put in force',
    cancel: 'Back',
    activated: 'Version {version} is in force.',
    why: 'A reason of at least {min} letters and the 6-digit code are needed.',
  },

  system: {
    title: 'System',
    /** S6: the compliance adapters. */
    compliance: {
      cargos: 'CaRGOS',
      off: 'CaRGOS is switched off (cargos.enabled): no report is required.',
      port: {
        send: 'Reports are sent by the app.',
        check: 'Only the trial from the app (CARGOS_MODE=check): cars are reported on the portal.',
        pending:
          'The credentials have not arrived yet: reports are made on the CaRGOS portal or by PEC.',
      },
      codes: 'Code tables',
      codesValue: 'of {date}, {places} places',
      codesHash: 'Fingerprint of the code tables: {hash}',
      codesStale: 'The code tables are {days} days old: they need refreshing (RUNBOOK).',
      last24h: 'Last 24 hours',
      none: 'no report',
      oldestQueued: 'Queued since',
      stuck: 'A report has been stuck since {when}: it has not reached CaRGOS.',
      fiscal: 'Fiscal documents',
      fiscalPending: 'No provider chosen: document numbers are typed from the electronic till’s.',
      fiscalFake:
        'Stand-in provider (local and test runs only): the documents it issues are not real.',
      fiscalOnline: {
        none: 'Online payments: nothing is asked of the provider (fiscal.online_payment_document).',
        at_payment: 'Online payments: the receipt is asked of the provider when the money is paid.',
      },
      fiscalWaiting: 'Requests still without a document',
      fiscalNoneWaiting: 'none',
      fiscalStatus: {
        queued: 'Queued',
        issued: 'Issued',
        refused: 'Refused',
        unknown: 'Outcome unknown',
        cancelled: 'Cancelled',
      },
    },
    intro:
      'The state of the services behind the app: backups, outgoing messages, alerts. It refreshes every minute.',
    checkedAt: 'Checked at {time}',
    state: { ok: 'Working', attention: 'Needs attention' },
    never: 'never',
    failingTitle: 'Not working',
    failing: {
      database: 'The database',
      worker: 'The background service (e-mails, alerts)',
      delivery: 'Sending e-mail',
      backups: 'The backups',
    },
    backups: {
      title: 'Backups',
      pending: 'No off-site storage for backups is configured yet.',
      base: 'Latest base backup',
      verify: 'Latest check',
      verifyFailed: 'failed',
      files: 'Latest copy of photos and documents',
      filesCounts: '{copied} copied, {failed} failed, {total} in all',
      archiver: 'Continuous archiving',
      archiverOk: 'Working',
      archiverFailing: 'Failing: {count} errors, the latest on {when}',
      heartbeat: 'Latest sign of life',
      busy: {
        base: 'taking the base backup',
        files: 'copying photos and documents',
      },
    },
    worker: {
      title: 'Background service',
      heartbeat: 'Latest sign of life',
      jobs: 'Periodic checks',
      lastSuccess: 'Last succeeded: {when}',
      failures: '{count} failures in a row',
      lastError: 'Last error of “{job}”: {error}',
      job: {
        heartbeat: 'Sign of life',
        'alerts.evaluate': 'Alert evaluation',
        'smtp.probe': 'Mail server',
        'daily.schedule': 'Daily jobs',
        'cargos.erase': 'Erasing ID numbers of ended bookings',
        'notifications.schedule': 'Reminders and alerts',
      },
    },
    daily: {
      title: 'Daily jobs',
      morning: 'The Owner’s morning e-mail',
      next: 'Next: {when}',
      pending: {
        no_email: 'There is no mail server (SMTP).',
        no_opening_hours: 'No pick-up point has opening hours.',
        no_stripe: 'Stripe’s keys are not set.',
      },
      reconcile: 'The night’s check against Stripe',
      unknown: 'The background service has not worked it out yet.',
      last: 'Last: {date}, {status}',
      status: { pending: 'on its way', delivered: 'done', dead: 'failed' },
    },
    /** S7: the customer reminders and the staff alerts, as the background service last read them. */
    /** S8: "Conservazione dei dati": what the retention runs do and what keeps the rest. */
    privacy: {
      title: 'Data retention',
      intro:
        'What the background service erases by itself, every hour, and what keeps the rest. Numbers only, never a name.',
      allOff:
        'No period is set: for now only the contact details of whoever asks are erased, 24 hours after the request.',
      periodsTitle: 'Periods',
      periods: {
        identityDocuments: 'Document photos',
        vehiclePhotos: 'Vehicle photos',
        fineWindow: 'Waiting for fines',
        customer: 'Customers',
      },
      days: { one: '{count} day', other: '{count} days' },
      after: {
        identityDocuments: '{days} after the rental ends',
        vehiclePhotos: '{days} after the rental ends',
        fineWindow: '{days} after the return, before the identity is erased',
        customer: '{days} after the last activity',
      },
      off: {
        identityDocuments: 'Off: they are not erased by themselves',
        vehiclePhotos: 'Off: they are not erased by themselves',
        fineWindow: 'Not set: customers’ identities stay',
        customer: 'Off: customers are erased only on request',
      },
      dueTitle: 'Past their date',
      due: {
        data: 'Data',
        erasable: 'To erase',
        held: 'Kept',
        why: 'Why',
        identityDocuments: 'Document photos',
        vehiclePhotos: 'Vehicle photos',
        contacts: 'Customers’ contact details',
        identities: 'Customers’ identities',
        importRows: 'Imported rows with no customer',
        off: 'off',
        none: '—',
        hold: '{hold}: {count}',
      },
      lastRunTitle: 'Last run',
      lastRun: {
        never: 'The background service has not run a retention pass yet.',
        when: 'When',
        files: 'Files',
        filesValue: '{erased} erased, {held} kept, {failed} failed',
        customers: 'Customers',
        customersValue:
          '{contacts} contact details and {identities} identities erased, {held} kept, {failed} failed',
        bytes: 'Removed from disk',
        bytesValue: '{removed} files, {failed} failed',
        importRows: 'Imported rows emptied',
        replies: 'Saved replies emptied',
        stripe: 'Stripe customers queued',
      },
      unfinishedTitle: 'Not finished yet',
      unfinished: {
        bytes: 'Erased files still on disk',
        copies: 'Backup copies not yet overwritten',
        since: '{count}, the oldest since {when}',
        none: 'none',
        backupsOff: 'not watched: backups are off',
      },
      requestsTitle: 'Requests',
      requests: {
        open: 'Open erasure requests',
        openValue: '{open}, {inGrace} of them still within the 24 hours',
        oldest: 'The oldest recorded',
        legalHolds: 'Customers under a legal hold',
        stripe: 'Stripe customers to delete',
        stripeValue: '{queued} queued, {unqueued} to queue, {dead} failed',
        stripeDead: '{count} deletions at Stripe failed: the RUNBOOK says how to retry them.',
      },
      orphans: {
        never: 'The daily check for files with no row has not run yet.',
        line: 'Last check for files with no row on {when}: {unknown} unknown, {failed} failed. They are never removed by themselves: the RUNBOOK says how.',
      },
    },
    notifications: {
      title: 'Reminders and alerts',
      pickup: 'Pick-up reminder to the customer',
      return: 'Return reminder to the customer',
      late: 'Vehicle-not-back alert',
      alerts: 'Deposit and CaRGOS alerts',
      hoursBefore: '{hours} hours before',
      minutesAfter: '{minutes} minutes after the booked time',
      on: 'On',
      off: {
        not_configured: 'Off: no value set ({key}).',
        no_email: 'There is no mail server (SMTP).',
      },
      unknown: 'The background service has not read it yet.',
      recipients: 'Staff alerts go to {it} in Italian and {en} in English.',
      noRecipients:
        'Nobody would receive the staff alerts: the operations.alerts permission is needed (RUNBOOK).',
    },
    outbox: {
      title: 'Outgoing messages',
      pending: 'Queued',
      oldest: 'Oldest waiting since',
      retrying: 'Being retried',
      dead: 'Not sent',
      deadTitle: 'Latest messages not sent',
      noneDead: 'No unsent messages.',
      topic: 'Kind',
      attempts: 'Attempts',
      when: 'When',
      error: 'Error',
      emails: {
        title: 'E-mails not sent',
        intro:
          'Each can be sent again: before it leaves the server reads the facts again, and if they no longer hold nothing is sent.',
        line: 'Queued on {queued}; not sent after {attempts} attempts.',
        others:
          'The other messages not sent are not e-mails and are not sent from here: the RUNBOOK says how to recover them.',
      },
      kinds: {
        'booking.confirmed': 'Booking confirmation, to the customer',
        'rental.returned': 'Rental summary, to the customer',
        'payment_link.requested': 'Payment link, to the customer',
        'contract.email': 'Signed contract, to the customer',
        'reminder.pickup': 'Pick-up reminder, to the customer',
        'reminder.return': 'Return reminder, to the customer',
        'booking.cancelled': 'Booking cancellation, to the customer',
        'refund.succeeded': 'Refund, to the customer',
        'operations.alert': 'Operational alert, to the staff',
        'money.morning': 'Morning e-mail, to the Owner',
        'dispute.notify': 'Dispute by the bank, to the Owner',
        'contact.message': 'Website contact form message, to the office',
      },
      resend: {
        action: 'Send again',
        label: 'Send again: {what}',
        title: 'Send this e-mail again?',
        queued: 'Queued on {when}: whoever receives it reads it late.',
        recheck:
          'Before it leaves the server reads the facts again: if they no longer hold (the pick-up has passed, the link has expired, the refund did not go through, the customer was erased, a website message is over 30 days old and was erased), nothing is sent.',
        confirm: 'Send again',
        keep: 'Don’t send',
        done: 'Queued again. The background service reads the facts again and, if they still hold, sends it. If sending fails again, it retries for some hours, then comes back to this list.',
      },
    },
    alerts: {
      title: 'Alerts',
      none: 'No alerts.',
      since: 'since {when}',
      recipients: 'Alert e-mails go to {it} in Italian and {en} in English.',
      noRecipients: 'Nobody would receive the alert e-mails.',
    },
    reporting: {
      title: 'Error reporting',
      on: 'On: {sent} sent, {dropped} dropped since the server started.',
      pending: 'No service chosen: errors stay in the server’s logs.',
      sessionsRefused:
        'The chosen service does not accept sessions: the crash-free rate is not measured.',
    },
    disk: {
      title: 'Disk space',
      free: '{percent}% free',
      unknown: 'Not available',
    },
    releases: 'Versions: API {api}, background service {worker}',
    banner: 'Something in the system needs attention.',
    bannerLink: 'Open System',
  },

  /** S2: what each alert means, for its e-mail and the Sistema screen. */
  alerts: {
    'backups.base_stale': {
      title: 'No base backup for more than 26 hours',
      todo: 'Without recent backups a server failure would lose data. See the runbook.',
    },
    'backups.verify_failed': {
      title: 'Backup verification failed',
      todo: 'Some restore points may be unusable. See the runbook.',
    },
    'backups.files_failed': {
      title: 'Copying photos and documents to the bucket fails',
      todo: 'New photos and documents have no copy yet. See the runbook.',
    },
    'backups.archiving_failing': {
      title: 'WAL archiving is failing',
      todo: 'The server’s disk fills until it is fixed: act now. See the runbook.',
    },
    'backups.silent': {
      title: 'The backup container is silent',
      todo: 'See the runbook.',
    },
    'outbox.overdue': {
      title: 'Customer e-mails are overdue',
      todo: 'The worker that sends them has stopped. They leave once it runs again.',
    },
    'outbox.dead': {
      title: 'Some messages were not sent',
      todo: 'The list is on the Sistema screen: contact the customers concerned if needed.',
    },
    'disk.low': {
      title: 'The server is running out of disk space',
      todo: 'See the runbook.',
    },
    'privacy.erasures_stuck': {
      title: 'Files erased for privacy are still kept',
      todo: 'Photos or documents erased for privacy are still on the server or in its backups. See the runbook.',
    },
  },

  /**
   * S5 (PAY-6, Fady 15 Oct): a drawer per pick-up point, opened with a count,
   * closed with a blind one; cash moves only through an open drawer.
   */
  cash: {
    title: 'Cash',
    intro:
      'One drawer per pick-up point. It opens with a count of the cash in it, and closes with a blind count: what it should hold shows only after the count.',
    state: { closed: 'Closed', open: 'Open', counting: 'Closing' },
    openedAt: 'Opened',
    openedLine: '{date}, {name}',
    openingCount: 'Cash at opening',
    depositsHeldLabel: 'Cash deposits kept here',
    // S9 (Q-62): cash recorded here from a trip while still in the pocket of whoever drove.
    outsideTill: {
      title: 'Cash taken outside the till',
      intro:
        'Taken or given out on trips and recorded into this drawer while still in the pocket of whoever drove: it goes into the drawer before the count.',
      taken: 'taken {amount}',
      given: 'given to customers {amount}',
      handIn: 'to put in the drawer {amount}',
      giveBack: 'to give back from the drawer {amount}',
      even: 'nothing to put in the drawer',
    },
    openCount: 'Cash in the drawer at {point}',
    openCountHint: 'Count it, then enter the total.',
    depositsHeld: 'Cash deposits worth {amount} are kept here.',
    openBelowDeposits:
      'The count is less than the cash deposits kept here ({amount}): check again before opening.',
    openSubmit: 'Open the drawer',
    closeTitle: 'Closing',
    closeCount: 'Cash counted',
    blindHint: 'What it should hold shows only after the count.',
    countSubmit: 'Record the count',
    counted: 'First count: {first}. It should hold: {expected}. Difference: {difference}.',
    frozen: 'From now on the drawer takes and gives back no cash until it is closed.',
    finalCount: 'Final count',
    differenceNow: 'Difference: {difference}',
    reason: 'Reason for the difference',
    reasonHint: 'The Owner reads it.',
    closeSubmit: 'Close the drawer',
    closed: 'The {point} drawer is closed.',
    opened: 'The {point} drawer is open.',
    openedElsewhere:
      'The {point} drawer had already been opened on another device: its count stands.',
    openTitle: 'Opening',
    /** UI-2g: how Cassa's points arrive, as Veicoli says of its vehicles. */
    noPointFills: 'Pick-up points come with the company’s configuration.',
    why: {
      count: 'Enter the cash counted, for example 50 or 50.50.',
      reason: 'Enter the reason for the difference, at least 3 characters.',
    },
    history: {
      title: 'Latest closes',
      none: 'No drawer closed yet.',
      point: 'Point',
      closedAt: 'Closed',
      opening: 'At opening',
      firstCount: 'First count',
      finalCount: 'Final count',
      expected: 'Expected',
      difference: 'Difference',
      reason: 'Reason',
    },
    field: {
      legend: 'Cash drawer',
      point: 'The drawer’s pick-up point',
      choose: 'Choose the drawer: this device will remember it.',
      isOpen: 'The {point} drawer is open.',
      isClosed: 'The {point} drawer is closed: open it with a count of the cash in it.',
      isCounting:
        'The {point} drawer is being closed: until it is, it takes and gives back no cash.',
      toCash: 'Go to Cash',
      noAccess: 'The drawer is opened by whoever records payments: ask someone who can.',
      noPoint: 'No pick-up point is in service.',
      // S9: cash taken on a trip, into a drawer the office keeps open.
      tripInto: 'The cash goes into the {point} drawer: leave it there when you are back.',
      tripChoose: 'Choose the drawer the cash goes into.',
      tripNone: 'No drawer is open: the cash is recorded once the office opens one.',
    },
  },

  /** S5: the Owner's morning e-mail (Fady, 15 Oct): codes and plates, no names. */
  morningEmail: {
    subject: '[Mazzella Rent] The morning round-up, {date}',
    intro: 'To look at today:',
    holds: 'Card deposits lapsing or lapsed:',
    bookingVehicle: '{code}, vehicle {plate}',
    holdLapsing: '{booking}: the deposit lapses on {when}.',
    holdLapsed: '{booking}: the deposit lapsed on {when}.',
    holdLapsedOut: '{booking}: the deposit lapsed on {when}, with the vehicle still out.',
    disputes: 'Disputes to answer in Stripe’s Dashboard:',
    outside: 'a payment the app has not recorded',
    disputeAnswer: '{booking}: {amount}, answer by {when} ({id}).',
    disputeUnknown: '{booking}: {amount}, Stripe status {status} ({id}).',
    drawers: 'Cash drawers, to look at on the Cash screen:',
    drawerOpen: '{point}: opened on {when} and not closed yet.',
    drawerCounting: '{point}: opened on {when}, counted and not closed yet.',
    drawerClose:
      '{point}, closed on {when}: first count {first}, final count {counted}, it should have held {expected} (difference {difference}).',
    stripe: 'The night’s check against Stripe:',
    stripeMissing: 'The check of {date} has not run: see the System screen.',
    stripeRepaired: '{booking}: the check recorded {what} ({id}).',
    stripeRepairedWhat: {
      payment: 'a payment',
      refund: 'a refund',
      dispute: 'a dispute',
    },
    stripeDifference: '{booking}: {what}, {amount} ({id}), since {date}.',
    stripeMore: '…and {count} more, on the Reconciliation screen.',
    noBooking: 'no booking',
    cargos: 'CaRGOS, to look at in the booking’s hand-over:',
    cargosProblem: {
      refused: '{booking}: CaRGOS refused the report ({when}).',
      unknown: '{booking}: the CaRGOS report’s outcome is unknown ({when}): check the portal.',
      queued: '{booking}: the CaRGOS report has been stuck since {when}.',
    },
    cargosEnded: {
      cancelled:
        '{booking}: booking cancelled yesterday, and CaRGOS {status}: check on the portal whether to withdraw it.',
      no_show:
        '{booking}: no-show yesterday, and CaRGOS {status}: check on the portal whether to withdraw it.',
    },
    cargosEndedStatus: {
      sent: 'has the report the app sent',
      recorded: 'has the report recorded from the portal or the PEC',
      unknown: 'may have the report (outcome unknown)',
    },
    cargosReceipts: 'Yesterday’s CaRGOS receipts, to tick against the portal’s daily PDF:',
    cargosReceipt: '{booking}: receipt {receipt} ({channel}, {when}).',
    cargosChannel: {
      api: 'from the app',
      portal: 'from the portal',
      pec: 'from the PEC',
    },
    cargosMoreReceipts: '…and {count} more, each on its booking.',
    fiscal: 'Tills and fiscal documents, to look at in Cash and on the Fiscal screen:',
    fiscalZ:
      '{point}, Z no. {number} of {when}: total {total}, difference from the recorded documents {difference}.',
    fiscalZMoved:
      '{point}, Z no. {number} of {when}: total {total}, difference {difference} when it was typed, {now} with the documents recorded since.',
    fiscalUnclosed:
      '{point}: {count} till documents ({amount}) after Z no. {number} of {when}: a closure missing?',
    fiscalUnclosedNoZ: '{point}: {count} till documents ({amount}) and no Z recorded.',
    fiscalNumbering:
      '{point}: Z no. {next} of {nextWhen} comes after no. {previous} of {previousWhen}: a number skipped or out of order.',
    fiscalMissing:
      'Without a fiscal document: {count} items, {amount} (yesterday’s: {yesterdayCount}, {yesterdayAmount}).',
    fines: 'Fines whose driver’s details are still to be sent, on the Fines screen:',
    fineDueSoon: '{authority}, notice {number} (plate {plate}, {code}): due on {date}.',
    fineOverdue: '{authority}, notice {number} (plate {plate}, {code}): overdue since {date}.',
    fineNoBooking: 'no rental matched',
    where: 'The deposits are on the Today screen of the staff app too.',
    automatic: 'Automatic message: do not reply to this e-mail.',
  },
  reports: {
    title: 'Reports',
    intro:
      'Money by day or month and by category, and what is still open. Booking codes and plates, never customers’ names.',
    tabs: { revenue: 'Revenue', open: 'Open' },
    from: 'From',
    to: 'To',
    by: 'By',
    byDay: 'day',
    byMonth: 'month',
    show: 'Show',
    csv: 'Download the CSV',
    csvPending: 'CSV in the iPad app: download it from the web app.',
    generatedAt: 'As of {when}',
    /** UI-2g: said once a period's answer is in. */
    shown: 'Revenue from {from} to {to}, by {by}.',
    empty: 'Nothing in this period.',
    categoryGone: 'Category no longer listed',
    total: 'Total',
    period: 'Period',
    category: 'Category',
    received: 'Received',
    rented: 'Rented',
    channels: {
      cash: 'Cash',
      pos: 'Card terminal',
      transfer: 'Bank transfer',
      stripe: 'Stripe',
    },
    handovers: 'Rentals handed over',
    handedOver: 'Value handed over',
    fees: 'Fees kept',
    waived: 'Fees waived',
    fineFees: 'Fines handling fees',
    note: 'Gross: the VAT regime is not decided yet. Refunds count on the day they went through, lost disputes on the day they were opened, fines handling fees on the day they were charged.',
    open: {
      deposits: 'Deposits held or waiting',
      receivable: 'To collect',
      payable: 'To give back',
      disputes: 'Open disputes',
      fiscal: 'Without a fiscal document',
      fines: 'Fines to handle',
      finesCount: 'Overdue: {overdue}. Due within 7 days: {dueSoon}.',
      shown: '{shown} of {count}: the rest are in the CSV.',
      none: 'None.',
      section: 'Section',
      booking: 'Booking',
      plate: 'Plate',
      state: 'State',
      amount: 'Amount',
      due: 'Due',
      reference: 'Reference',
      moved: 'When the money moved',
      method: 'Method',
      depositStates: { pending: 'Waiting for the card', held: 'Held' },
      problems: {
        failed: 'Refund failed',
        requires_action: 'Refund waiting for an action',
        disputed: 'Held back by a dispute',
      },
    },
    files: { revenue: 'revenue', open: 'open' },
  },
  reconcile: {
    title: 'Reconciliation',
    intro:
      'Every night the app checks the day before’s Stripe movements against its own: it records what Stripe did not manage to tell it, and lists here what needs a look.',
    pending: 'Stripe: without its keys there is nothing to check against.',
    date: 'Day',
    dayBefore: 'Day before',
    dayAfter: 'Day after',
    yesterday: 'Yesterday',
    due: 'The check of {date} is due {when}.',
    notRun: 'The check of {date} has not run: see the System screen.',
    run: 'Check of {date}, finished {when} ({trigger}).',
    triggers: { schedule: 'nightly', cli: 'started by hand' },
    runs: 'Checks of this day: {count}.',
    testMode: 'Stripe test mode.',
    totals: {
      transactions: 'Movements',
      charges: 'Payments',
      refunds: 'Refunds',
      disputes: 'Disputes',
      fees: 'Stripe fees',
      payouts: 'Payouts to the bank',
      other: 'Other',
      net: 'Net',
      outside: 'Outside the app',
    },
    repairs: 'Recorded by the check',
    repairKinds: { payment: 'Payment', refund: 'Refund', dispute: 'Dispute' },
    outcomes: {
      payment_recorded: 'recorded',
      refund_recorded: 'recorded',
      refund_updated: 'brought up to date',
      dispute_recorded: 'recorded',
      dispute_updated: 'brought up to date',
    },
    categories: {
      charge: 'Payment',
      partial_capture_reversal: 'Part of a deposit let go',
      refund: 'Refund',
      refund_failure: 'Refund that failed',
      dispute: 'Dispute',
      dispute_reversal: 'Dispute reversed',
    },
    noRepairs: 'Nothing to record.',
    outcomeUnknown: 'with an outcome the app does not know',
    categoryUnknown: 'A kind of movement the app does not know',
    outside: 'Money taken or given back outside the app',
    outsideHint:
      'Stripe movements with no reference of the app’s (the old website, the Dashboard): listed apart, they are not differences.',
    noOutside: 'None.',
    open: 'Differences to look at',
    openHint: 'Not read and still found, oldest first.',
    noOpen: 'No differences to look at.',
    more: '{count} more: the oldest are at the top.',
    ofDate: 'Found on this day',
    noneOfDate: 'None.',
    since: 'since {date}',
    resolved: 'No longer found since {when}.',
    acknowledged: 'Read by {name} on {when}: {reason}',
    reason: 'What you checked or did',
    reasonHint: 'At least 3 characters: it stays in the log, with your name.',
    acknowledge: 'Mark as read',
    why: 'Write what you checked or did, at least 3 characters.',
    acknowledgedDone: 'Difference marked as read.',
    detail: {
      category: 'Stripe movement: {category}',
      appAmount: 'Recorded in the app: {amount}',
      kept: 'Kept by Stripe: {stripe}; in the app: {app}',
      depositState: 'Deposit in the app: {state}',
      refundStatus: 'Refund in the app: {app}; at Stripe: {stripe}',
      stripeRefund: 'Refund at Stripe: {status}',
    },
    depositStates: {
      pending: 'waiting for the card',
      held: 'held',
      released: 'released',
      captured: 'captured',
      expired: 'expired',
      failed: 'failed',
    },
    minorUnits: '{amount} {currency}, in the currency’s smallest unit',
    /** UI-2g: how the movement is found in Stripe's Dashboard. */
    stripeReference: 'Stripe reference {id}',
    kinds: {
      payment_unrecorded: 'a payment Stripe took that the app could not record',
      amount_mismatch: 'a payment recorded for another amount',
      deposit_mismatch: 'a deposit Stripe kept for another amount',
      refund_unrecorded: 'money Stripe gave back that the app has not recorded',
      refund_status: 'a refund in another state than Stripe’s',
      dispute_unrecorded: 'a dispute the app could not record',
      foreign_reference: 'a movement naming something this app never made',
      unexpected: 'a movement of a kind the app does not know',
      currency: 'customer money in another currency',
    },
  },
  disputeEmail: {
    subject: {
      answer: '[Mazzella Rent] Dispute {code}: answer by {date}',
      lost: '[Mazzella Rent] Dispute lost: {code}',
      won: '[Mazzella Rent] Dispute won: {code}',
      other: '[Mazzella Rent] Dispute {code}: {status}',
    },
    intro: 'A customer’s bank is disputing a card payment taken with Stripe.',
    booking: 'Booking: {code}',
    bookingVehicle: 'Booking: {code}, vehicle {plate}',
    outside:
      'A payment the app has not recorded: taken outside the app, or not recorded yet (Stripe charge {charge}).',
    amount: 'Amount disputed: {amount}',
    reason: 'Reason given by the bank: {reason}',
    status: 'Status: {status}',
    answerBy: 'Answer in Stripe’s Dashboard by {date}.',
    open: 'While the bank decides, nothing of this payment goes back by any means, and the booking cannot be closed.',
    lost: 'The bank gave {amount} back to the customer: the booking owes it again, taken at the counter only.',
    won: 'The bank decided for the company: the money stays with the company.',
    ended: 'The dispute is closed: the money stays with the company.',
    where: 'In Stripe: https://dashboard.stripe.com/disputes, the dispute {id}.',
    automatic: 'Automatic message: do not reply to this e-mail.',
  },
  /** S2: the alert e-mails, to the holders of the Sistema permission. */
  alertEmail: {
    subject: {
      fired: '[Mazzella Rent] Attention: {title}',
      resolved: '[Mazzella Rent] Resolved: {title}',
      reminder: '[Mazzella Rent] Still unresolved: {title}',
      notice: '[Mazzella Rent] {title}',
    },
    intro: {
      fired: 'The system found a problem.',
      resolved: 'The problem reported earlier is resolved.',
      reminder: 'The problem reported earlier is still unresolved.',
      notice: 'The system found a problem.',
    },
    since: 'Since: {when}',
    todo: 'What to do: {todo}',
    screen: 'The details are on the staff app’s Sistema screen.',
    runbook: 'Runbook: docs/RUNBOOK.md, Alerts, {key}.',
    details: 'Technical details',
    automatic: 'Automatic message: please do not reply.',
  },
  /**
   * Q-165: a message from the website's contact form, to the office mailbox.
   * Reply-To is the sender, so the office answers with Reply.
   */
  contactEmail: {
    subject: '[Mazzella Rent] Message from the website: {name}',
    intro: 'A message from the website’s contact form.',
    name: 'Name: {name}',
    email: 'E-mail: {email}',
    language: 'Website language: {language}',
    message: 'Message:',
    reply: 'To answer, use Reply: the answer goes to the person who wrote.',
    languages: { it: 'Italian', en: 'English', de: 'German' },
  },
  /**
   * S7 (NTF-3): the operational alerts, to the holders of operations.alerts.
   * Codes and plates only: who the customer is, the app shows.
   */
  operationsEmail: {
    subject: {
      late_return: '[Mazzella Rent] Vehicle not returned: {code}',
      hold_lapsing: '[Mazzella Rent] Deposit hold lapsing: {code}',
      hold_lapsed: '[Mazzella Rent] Deposit hold lapsed, vehicle out: {code}',
      cargos: '[Mazzella Rent] CaRGOS to check: {code}',
      overbooked: '[Mazzella Rent] Web booking paid late, category full: {code}',
    },
    intro: {
      late_return: 'A vehicle has not come back at its booked time.',
      hold_lapsing:
        'A deposit’s card hold is about to lapse: after that, the deposit can no longer be captured.',
      hold_lapsed: 'A deposit’s card hold lapsed while the vehicle is still out.',
      cargos_refused: 'A hand-over’s CaRGOS report was refused.',
      cargos_unknown: 'A hand-over’s CaRGOS report has no clear answer.',
      cargos_queued:
        'A hand-over’s CaRGOS report has been waiting for more than {minutes} minutes.',
      overbooked:
        'A website booking was paid after its hold ran out: it is confirmed, but its category no longer has a vehicle for every booking on those days.',
    },
    booking: 'Booking: {code}',
    vehicle: 'Vehicle: {plate}',
    at: {
      late_return: 'Booked return: {when}',
      hold_lapsing: 'The hold lapses: {when}',
      hold_lapsed: 'Lapsed: {when}',
      cargos: 'Since: {when}',
      overbooked: 'Paid and confirmed: {when}',
    },
    todo: {
      late_return: 'What to do: contact the customer; their details are in the booking.',
      hold_lapsing:
        'What to do: before it lapses, decide whether to capture or release the deposit.',
      hold_lapsed:
        'What to do: the deposit no longer secures the rental; decide how to proceed at the return.',
      cargos: 'What to do: open the booking and settle the report before the hand-over.',
      overbooked:
        'What to do: find a vehicle (another category will do) or contact the customer; the money stays taken.',
    },
    open: 'Open booking {code} in the staff app.',
    automatic: 'Automatic message: please do not reply.',
  },
  /** DOC-SCAN: scanning a licence or ID to prefill the hand-over form. */
  scan: {
    button: 'Scan document',
    title: 'Scan the document',
    intro:
      'Frame the document in the box, on a dark background with no glare, or choose a photo. Everything stays on this device: nothing is sent.',
    front: 'Front',
    back: 'Back (US licences)',
    captured: 'Captured: {side}',
    capture: 'Capture',
    choosePhoto: 'Choose a photo',
    readAction: 'Read the document',
    retake: 'Capture again',
    cancel: 'Cancel',
    useManual: 'Enter by hand',
    reading: 'Reading the document…',
    cameraOff:
      'The camera is unavailable: choose a photo of the document, or enter the details by hand.',
    failed: 'Could not read the document. Try again, or enter the details by hand.',
    timeout:
      'Reading took too long. Try again with a sharper, closer photo, or enter the details by hand.',
    invalidFile: 'The chosen file is not an image. Choose a photo of the document.',
    reviewTitle: 'Check and confirm',
    reviewIntro:
      'The fields read from the document are below. Uncertain ones are highlighted: tick each after checking it. A field already filled in the form keeps its value unless you choose to replace it. Nothing is saved until you press Confirm.',
    toCheck: 'To check',
    readFrom: 'Read from the document',
    current: 'Now in the form',
    replace: 'Replace with the read value',
    verified: 'Verified',
    mustVerify: 'Tick every field to check',
    confirm: 'Confirm the details',
    nothingRead: 'Nothing readable. Enter the details by hand.',
    partial: 'Only some fields were read: the others are typed by hand.',
    categoryWarning: 'The scanned licence may not cover the booked vehicle: check the category.',
    issue: {
      'licence-expired': 'The licence appears to have expired before the return date.',
      'licence-dates-order': 'The licence dates are inconsistent (expiry before issue).',
      'mrz-check-failed': 'The machine-readable zone does not check out: review the read values.',
      'licence-number-format': 'The licence number is not in the expected format: check it.',
      'name-mismatch': 'The name on the ID and the licence do not match: check.',
    },
  },
  paper: {
    title: 'From paper {code}',
    open: 'Type in from paper',
    intro:
      'For a rental kept on paper while the system did not answer. Type what the printed set says, with the day and time written on the sheet: those count, not the time you type them in.',
    window: 'What happened up to {days} days ago can be typed in.',
    revenueDay: {
      actual: 'Money counts on the day it was taken, as the receipt does.',
      entry: 'Money counts on the day you type it in.',
    },
    ownerOnly: 'Only the Owner types a rental in from paper.',
    notConfigured:
      'Typing in from paper is not on: the settings reentry.max_days and reentry.revenue_day are missing.',
    nothing: 'A booking in this state has nothing to type in from paper.',
    parts: 'What the set records',
    partHandover: 'The hand-over',
    partReturn: 'The return',
    handover: 'Hand-over',
    sheet: 'Set number',
    sheetHint: 'As printed at the top of the sheet, with its final letter.',
    vehicle: 'Vehicle that left',
    date: 'Day',
    time: 'Time',
    km: 'Kilometres',
    fuel: 'Fuel',
    documents: 'ID document and licence seen at the counter and copied onto the sheet',
    noIdentity:
      'First record who took the vehicle, from the hand-over screen, then come back here.',
    toIdentity: 'Record who took the vehicle',
    noSecondIdentity:
      'The booking has a second driver: first record their details, from the hand-over screen, then come back here.',
    toSecondIdentity: 'Record the second driver',
    receipt: 'CaRGOS receipt',
    receiptIntro:
      'The vehicle left once reported on the portal or by PEC: type the receipt and when it came.',
    receiptChannel: 'Reported through',
    receiptChannels: { portal: 'CaRGOS portal', pec: 'PEC' },
    receiptNumber: 'Receipt or protocol number',
    receiptOnFile: 'The CaRGOS receipt for this vehicle is already recorded.',
    blocked: 'The vehicle left although {blocks}.',
    overrideReason: 'Why the Owner let it leave',
    return: 'Return',
    kmHint: 'At the hand-over: {km} km.',
    notes: 'Notes on the sheet',
    payments: 'Money taken',
    noPayments: 'No money on this set.',
    addPayment: 'Add a payment',
    removePayment: 'Remove payment {number}',
    paymentNumber: 'Payment {number}',
    paymentsFull: 'At most {max} payments per set.',
    amount: 'Amount (€)',
    method: 'Method',
    reference: 'Reference (terminal slip, transfer)',
    drawerHint: 'Cash goes into the drawer open now, with the day and time it was taken.',
    owner: 'The Owner',
    reason: 'Why it was kept on paper',
    reasonHint: 'Kept with every part of the set in the change log.',
    code: 'Owner’s code',
    submit: 'Type the set in',
    railTitle: 'To type the set in',
    railDetails: 'The account',
    moneyHint: 'The account before the set: the payments typed in reduce it.',
    done: 'Set typed in from paper.',
    why: {
      empty: 'Choose what the set records: the hand-over, the return or a payment.',
      sheet: 'Type the set number as printed.',
      vehicle: 'Choose the vehicle that left.',
      identity: 'First record who took the vehicle.',
      secondIdentity: 'First record the second driver’s details.',
      documents: 'Confirm the documents were seen at the counter.',
      receiptChannel: 'Choose how CaRGOS was told.',
      receiptNumber: 'Type the CaRGOS receipt number.',
      receiptTime: 'Type the day and time of the CaRGOS receipt.',
      handoverTime: 'Type the day and time of the hand-over.',
      kmOut: 'Type the kilometres at the hand-over.',
      fuelOut: 'Choose the fuel at the hand-over.',
      overrideReason: 'Type why the vehicle left anyway, at least 3 characters.',
      returnTime: 'Type the day and time of the return.',
      kmIn: 'Type the kilometres at the return.',
      kmBelow: 'Check the kilometres at the return: they are below those at the hand-over.',
      fuelIn: 'Choose the fuel at the return.',
      paymentTime: 'Type the day and time of every payment.',
      paymentAmount: 'Type the amount of every payment.',
      paymentMethod: 'Choose the method of every payment.',
      drawer: 'Open the drawer for the cash.',
      reason: 'Type why it was kept on paper, at least 3 characters.',
      code: 'Type the Owner’s 6-digit code.',
    },
    handedOver: 'From paper, set {sheet}: typed in on {date}. {reason}',
    returned: 'Return from paper: typed in on {date}. {reason}',
    paidOnPaper: 'From paper: typed in on {date}.',
  },

  fallbackKit: {
    title: 'Emergency kit',
    intro:
      'The forms for renting on paper when the system does not answer. Print them beforehand, while everything works, and keep a stock at the counter.',
    sets: 'Sets to print',
    setsHint:
      'One set per rental: contract, hand-over, return, deposit and payments, CaRGOS. From 1 to {max}.',
    prepare: 'Prepare the kit',
    preparing: 'Kit {kit} is being prepared.',
    asked: 'Kit {kit} asked for: {sets} sets, {first} to {last}.',
    ready: 'Kit {kit} ready: {sets} sets, terms version {version}.',
    failed: 'Kit {kit} was not prepared. Ask for a new one; the System screen says why.',
    open: 'Open kit {kit}',
    notInApp: 'The kit opens in the office computer’s browser, to be printed.',
    recent: 'Recent kits',
    none: 'No kit yet: prepare one now, while the system answers.',
    range: 'Sets {first} to {last}',
    reprint:
      'When the Owner puts new rental terms in force, print a new kit and destroy the old kit’s unused sets.',
    pdf: {
      title: 'Emergency kit: renting on paper',
      printed: 'Kit {kit} · printed on {date}',
      terms: 'Rental terms in force: version {version}.',
      draft: 'DRAFT',
      draftNotice: 'DRAFT terms: this kit is for testing only and has no legal value.',
      setRange: 'Sets numbered {first} to {last}.',
      whenTitle: 'When to use it',
      when: 'Only when the system does not answer at the counter. While it answers, every rental is made in the app as usual.',
      stepsTitle: 'What to do',
      step1:
        'One set per rental. Its number is the contract’s number: write it on the file and use it for nothing else.',
      step2:
        'Fill in the contract with the customer, who signs twice: the contract and the specific approval of the clauses.',
      step3:
        'Where the category requires it, report the rental to CaRGOS (the set’s CaRGOS sheet). Without a receipt the vehicle does not leave, as in the app.',
      step4:
        'At the hand-over: time, km, fuel in eighths, damages zone by zone, the customer’s signature. The same at the return.',
      step5:
        'Deposit and payments: one line per amount taken, with time, amount, method, receipt or invoice number and who took it. Cash stays in the open drawer.',
      step6:
        'When the system is back, the Owner enters each set with its real times (Paper re-entry). Until then the filled-in sets are the only original: keep them in number order.',
      set: 'Set no. {number}',
      footer: '{sheet} · set no. {number} · terms version {version}',
      sheets: {
        contract: 'Rental contract',
        handover: 'Vehicle hand-over',
        return: 'Vehicle return',
        payments: 'Deposit and payments',
        cargos: 'CaRGOS report',
      },
      sections: {
        booking: 'Booking',
        driver: 'Driver',
        document: 'ID document',
        licence: 'Driving licence',
        vehicle: 'Vehicle',
        period: 'Period and places',
        price: 'Price',
        signatures: 'Signatures',
        deposit: 'Deposit',
        payments: 'Payments',
      },
      fields: {
        bookingCode: 'Booking code, if there is one',
        surname: 'Surname',
        givenNames: 'Given names',
        birthDate: 'Date of birth',
        birthPlace: 'Place of birth',
        citizenship: 'Citizenship',
        residenceAddress: 'Home address: street and number',
        residencePostalCode: 'Postal code',
        residenceCity: 'Town or city',
        residenceCountry: 'Country',
        phone: 'Phone',
        email: 'E-mail',
        idDocumentKind: 'Document type',
        idDocumentNumber: 'Document number',
        idDocumentIssuedBy: 'Issued by (place)',
        licenceNumber: 'Licence number',
        licenceIssuedBy: 'Issued by (place)',
        licenceIssuedOn: 'Issue date',
        licenceExpiresOn: 'Expiry date',
        licenceCategories: 'Categories',
        vehiclePlate: 'Plate',
        vehicleModel: 'Make and model',
        pickupAt: 'Pick-up: day and time',
        pickupPlace: 'Pick-up place',
        returnDueAt: 'Return due: day and time',
        returnPlace: 'Return place',
        cover: 'Cover chosen',
        extras: 'Extras',
        totalAgreed: 'Agreed price (total)',
        signedAt: 'Signed on (day and time)',
        customerSignature: 'Customer’s signature',
        specificApprovalSignature: 'Signature for the specific approval',
        staffName: 'For the company (name)',
        checkedOutAt: 'Handed over on (day and time)',
        kmOut: 'Km at hand-over',
        fuelOut: 'Fuel or battery at hand-over',
        damagesOut: 'Damages already there',
        notesOut: 'Notes',
        staffOut: 'Handed over by',
        customerSignatureOut: 'Customer’s signature',
        returnedAt: 'Returned on (day and time)',
        kmIn: 'Km at return',
        fuelIn: 'Fuel or battery at return',
        damagesIn: 'New damages',
        notesIn: 'Notes',
        staffIn: 'Taken back by',
        customerSignatureIn: 'Customer’s signature',
        depositAmount: 'Deposit amount',
        depositMethod: 'How it was taken',
        depositTakenAt: 'Taken on (day and time)',
        depositReturnedAt: 'Given back on (day and time)',
        depositKept: 'Kept (with the reason in the notes)',
        paidAt: 'Day and time',
        paymentAmount: 'Amount',
        paymentMethod: 'Method',
        fiscalDocument: 'Receipt or invoice no.',
        receivedBy: 'Taken by',
        cargosChannel: 'Reported through',
        cargosSentAt: 'Reported on (day and time)',
        cargosReceipt: 'Receipt or protocol number',
        cargosReceiptAt: 'Receipt dated (day and time)',
        cargosSentBy: 'Reported by',
      },
      options: {
        idDocumentKind: {
          id_card: 'Identity card',
          passport: 'Passport',
          driving_licence: 'Driving licence',
          other: 'Other',
        },
        depositMethod: {
          pos: 'Hold on the shop’s POS',
          cash: 'Cash',
          none: 'None (the Owner decides)',
        },
        paymentMethod: {
          cash: 'Cash',
          card_terminal: 'Card on the POS',
          bank_transfer: 'Bank transfer',
        },
        cargosChannel: {
          portal: 'CaRGOS portal',
          pec: 'PEC',
        },
      },
      fuelScale: '0 = empty · 8 = full',
      zone: 'Zone',
      seen: 'What is seen (scratches, dents, breaks)',
      zonesNote:
        'Write “none” where there is nothing: an empty line does not say whether the zone was looked at. A scooter has no roof or interior; a car has no seat.',
      notesNote: 'Amounts kept, reasons, corrections: all here, with the writer’s initials.',
      cargosIntro:
        'Where the vehicle’s category requires it, the rental is reported to CaRGOS before the vehicle leaves, with this set’s contract details. Where it does not, cross this sheet out.',
      cargosPortal:
        'From the portal cargos.poliziadistato.it, with the company’s credentials: write down the receipt number the portal shows.',
      cargosPec:
        'If the portal does not work either, by PEC to the Questura. The template and the address are still to be confirmed (open question Q-112): until you have them, the vehicle does not leave.',
      pecAddress: 'The Questura’s PEC address',
      termsTitle: 'Rental terms',
      approvalTitle: 'Specific approval (arts. 1341 and 1342 of the Civil Code)',
    },
  },
}
